import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Trophy, Users, Target, Award, Plus, Video, FileText, StickyNote, Medal, Clock, TrendingUp, Shield, Lock, LogOut, Eye, Edit, Trash2, Save, X, Download, FileDown, BarChart, BarChart3, Menu, CheckCircle2, Calendar, ExternalLink, Rocket, List, Play, MessageSquare, ArrowRight, Upload, Info, KeyRound, Mail, CheckCircle, UserPlus, HelpCircle, Diamond, Sparkles, PartyPopper } from "lucide-react";

// Shared utilities
import { IST_TIMEZONE, convertUTCToIST, convertUTCToISTDate, getCurrentISTForInput, convertUTCToISTForInput, getStoryPointsColor, getMemberStoryPointsForChallenge, API, BACKEND_URL } from "./components/shared/utils";

// Extracted dialog components
import { AdminLoginDialog } from "./components/dialogs";
import { MemberLoginDialog } from "./components/dialogs";
import { PasswordResetRequestDialog, PasswordResetConfirmDialog } from "./components/dialogs";

// Shared components
import { MemberAvatar } from "./components/shared";

// Extracted tab components
import TeamsTab from "./components/tabs/TeamsTab";

// Self-Assessment Module
import SelfAssessmentWidget from "./components/SelfAssessmentWidget";

// Feedback Received Widget
import FeedbackReceivedWidget from "./components/FeedbackReceivedWidget";

// Diamond Quest Dashboard
import QuestDashboard from "./components/QuestDashboard";

// Admin Assessment Manager
import AdminAssessmentManager from "./components/AdminAssessmentManager";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [loginAction, setLoginAction] = useState("");
  
  // State management
  const [stats, setStats] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [members, setMembers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [challengeLeaderboard, setChallengeLeaderboard] = useState([]);
  const [challengeStats, setChallengeStats] = useState({});
  const [selectedChallenge, setSelectedChallenge] = useState("overall");
  
  // Dialog states
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [showSubmissionDialog, setShowSubmissionDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [membershipFilter, setMembershipFilter] = useState("all");
  const [editingChallenge, setEditingChallenge] = useState(null);
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showAllSubmissions, setShowAllSubmissions] = useState(false);
  const [submissionCategoryFilter, setSubmissionCategoryFilter] = useState(null);
  const [bulkImportChallenge, setBulkImportChallenge] = useState("");
  const [bulkImportData, setBulkImportData] = useState("");
  const [showNotifyDialog, setShowNotifyDialog] = useState(null); // Stores challenge to notify about
  const [notifySettings, setNotifySettings] = useState({
    prompt_numbers: [],
    recipient_type: "all",
    track: "none",
    selected_member_ids: [],
    custom_message: "",
    send_immediately: true
  });
  
  // Form states
  const [newMember, setNewMember] = useState({ 
    name: "", 
    email: "", 
    track: "none",
    team_id: "",  // Optional team assignment during creation
    send_activation_email: true 
  });
  const [newChallenge, setNewChallenge] = useState({ 
    title: "", 
    description: "", 
    base_points: 100, 
    bonus_points: 100, 
    status: "active",
    task_count: 21,
    tasks: Array.from({length: 21}, (_, i) => `Task ${i + 1}`)
  });
  const [newSubmission, setNewSubmission] = useState({ 
    member_id: "", 
    challenge_id: "",
    task_number: 1,
    content_url: "", 
    submission_type: "video",
    submission_method: "social_media",
    submission_time: getCurrentISTForInput(),
    bonus_points: 0,
    timezone: "Asia/Kolkata",
    submission_category: "basic_submission",
    feedback_text: "",
    feedback_to_member_id: "",
    improvement_reflection: "",
    first_submission_id: "",
    latest_submission_id: "",
    submitted_by_admin_id: "",
    // SPARK Feedback fields
    spark_specific: "",
    spark_improvement: "",
    spark_actionable: "",
    spark_reflection: "",
    spark_continue: "",
    feedback_mode: "text"  // "text" or "video"
  });
  
  // Track completed tasks for duplicate prevention
  const [completedTasks, setCompletedTasks] = useState([]);
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("leaderboard");
  
  // PWA Install prompt state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [mainView, setMainView] = useState("community"); // "my-dashboard" or "community"
  
  // Member details view state
  const [viewingMember, setViewingMember] = useState(null);
  const [memberSubmissions, setMemberSubmissions] = useState([]);
  const [memberChallengeStats, setMemberChallengeStats] = useState(null);
  
  // Member progress view state
  const [memberProgressData, setMemberProgressData] = useState([]);
  const [viewingProgressMember, setViewingProgressMember] = useState(null);
  
  // Task details view state
  const [viewingTaskDetails, setViewingTaskDetails] = useState(null);
  const [challengeTaskProgress, setChallengeTaskProgress] = useState(null);

  // Story Points & Authentication state
  const [currentUser, setCurrentUser] = useState(null);
  const [showMemberLoginDialog, setShowMemberLoginDialog] = useState(false);
  const [storyPointsSummary, setStoryPointsSummary] = useState({});
  const [showTrackSelector, setShowTrackSelector] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  
  // Welcome popup state
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [selectedTrackForMonth, setSelectedTrackForMonth] = useState("");
  const [trackTasks, setTrackTasks] = useState([]);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);
  
  // Admin mass import state
  const [showMassImportDialog, setShowMassImportDialog] = useState(false);
  const [importCsvText, setImportCsvText] = useState("");
  const [importResults, setImportResults] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  
  // Password reset state
  const [showPasswordResetRequestDialog, setShowPasswordResetRequestDialog] = useState(false);
  const [showPasswordResetConfirmDialog, setShowPasswordResetConfirmDialog] = useState(false);
  const [passwordResetToken, setPasswordResetToken] = useState(null);
  
  // Team state (CSH Story Premier League)
  const [teams, setTeams] = useState([]);
  const [teamStats, setTeamStats] = useState({ total_teams: 0, top_team: null, rankings_preview: [] });
  const [teamLeaderboard, setTeamLeaderboard] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDialog, setShowTeamDialog] = useState(false);
  const [showTeamDetailDialog, setShowTeamDetailDialog] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: "" });
  const [editingTeam, setEditingTeam] = useState(null);
  const [showAddMemberToTeamDialog, setShowAddMemberToTeamDialog] = useState(false);
  const [teamToAddMember, setTeamToAddMember] = useState(null);
  
  // Check for password reset token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('reset_token') || urlParams.get('token');
    if (resetToken) {
      setPasswordResetToken(resetToken);
      setShowPasswordResetConfirmDialog(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Fetch pending approvals
  const fetchPendingApprovals = async () => {
    try {
      const response = await axios.get(`${API}/submissions/pending-approval`);
      setPendingApprovals(response.data);
    } catch (error) {
      console.error("Error fetching pending approvals:", error);
    }
  };

  // Approve submission
  const handleApproveSubmission = async (submissionId, notes, customPoints) => {
    try {
      const payload = {};
      if (notes) payload.admin_notes = notes;
      if (customPoints !== undefined) payload.custom_points = customPoints;
      await axios.post(`${API}/submissions/${submissionId}/approve`, payload);
      fetchPendingApprovals();
      fetchData();
    } catch (error) {
      console.error("Error approving submission:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Reject submission
  const handleRejectSubmission = async (submissionId, notes) => {
    try {
      await axios.post(`${API}/submissions/${submissionId}/reject`, { admin_notes: notes });
      fetchPendingApprovals();
      fetchData();
    } catch (error) {
      console.error("Error rejecting submission:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Fetch member's challenge task progress
  const fetchMemberChallengeProgress = async (challengeId, memberId) => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeId}/member-progress/${memberId}`);
      setChallengeTaskProgress(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching member challenge progress:", error);
      setChallengeTaskProgress(null);
      return null;
    }
  };

  // Fetch member progress data
  const fetchMemberProgress = async () => {
    try {
      const response = await axios.get(`${API}/member-progress`);
      setMemberProgressData(response.data);
    } catch (error) {
      console.error("Error fetching member progress:", error);
      setMemberProgressData([]);
    }
  };

  // Fetch teams data (CSH Story Premier League)
  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
      setTeams([]);
    }
  };

  const fetchTeamStats = async () => {
    try {
      const response = await axios.get(`${API}/teams/stats`);
      setTeamStats(response.data);
    } catch (error) {
      console.error("Error fetching team stats:", error);
    }
  };

  const fetchTeamLeaderboard = async (challengeId = null) => {
    try {
      const url = challengeId ? `${API}/teams/leaderboard?challenge_id=${challengeId}` : `${API}/teams/leaderboard`;
      const response = await axios.get(url);
      setTeamLeaderboard(response.data);
    } catch (error) {
      console.error("Error fetching team leaderboard:", error);
      setTeamLeaderboard([]);
    }
  };

  const fetchTeamDetails = async (teamId, challengeId = null) => {
    try {
      const url = challengeId ? `${API}/teams/${teamId}?challenge_id=${challengeId}` : `${API}/teams/${teamId}`;
      const response = await axios.get(url);
      setSelectedTeam(response.data);
      setShowTeamDetailDialog(true);
    } catch (error) {
      console.error("Error fetching team details:", error);
    }
  };

  // Fetch completed tasks for a member in a challenge (duplicate prevention)
  const fetchCompletedTasks = async (memberId, challengeId) => {
    if (!memberId || !challengeId) {
      setCompletedTasks([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/submissions/completed-tasks/${memberId}/${challengeId}`);
      setCompletedTasks(response.data.completed_tasks || []);
    } catch (error) {
      console.error("Error fetching completed tasks:", error);
      setCompletedTasks([]);
    }
  };

  // Fetch all data
  const fetchData = async () => {
    try {
      const [leaderboardRes, statsRes, challengesRes, membersRes, submissionsRes, storyPointsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/leaderboard`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/challenges`),
        axios.get(`${API}/members`),
        axios.get(`${API}/submissions`),
        axios.get(`${API}/dashboard/story-points-summary`),
        axios.get(`${API}/teams`)
      ]);

      setLeaderboard(leaderboardRes.data);
      setStats(statsRes.data);
      setChallenges(challengesRes.data);
      setMembers(membersRes.data);
      setSubmissions(submissionsRes.data);
      setStoryPointsSummary(storyPointsRes.data);
      setTeams(teamsRes.data);

      // Fetch member progress data
      await fetchMemberProgress();

      // Fetch challenge-specific data if a challenge is selected
      if (selectedChallenge && selectedChallenge !== "overall") {
        const [challengeLeaderboardRes, challengeStatsRes, teamStatsRes] = await Promise.all([
          axios.get(`${API}/challenges/${selectedChallenge}/leaderboard`),
          axios.get(`${API}/challenges/${selectedChallenge}/stats`),
          axios.get(`${API}/teams/stats?challenge_id=${selectedChallenge}`)
        ]);
        setChallengeLeaderboard(challengeLeaderboardRes.data);
        setChallengeStats(challengeStatsRes.data);
        setTeamStats(teamStatsRes.data);
        
        // Fetch team leaderboard for this challenge
        await fetchTeamLeaderboard(selectedChallenge);
      } else {
        // Clear team stats when viewing overall
        setTeamStats({ total_teams: teams.length, top_team: null, rankings_preview: [] });
        setTeamLeaderboard([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Check authentication on component mount
  useEffect(() => {
    const authToken = localStorage.getItem("dashboard_auth");
    const role = localStorage.getItem("dashboard_role");
    if (authToken === "true" && role) {
      setIsAuthenticated(true);
      setUserRole(role);
    }
  }, []);

  // PWA Install prompt handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install banner if not dismissed before
      const dismissed = localStorage.getItem('pwa_install_dismissed');
      if (!dismissed) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowInstallBanner(false);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle PWA install
  const handleInstallApp = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
    }
    setDeferredPrompt(null);
  };

  // Dismiss install banner
  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa_install_dismissed', 'true');
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
    if (isAuthenticated || currentUser?.is_admin) {
      fetchPendingApprovals();
    }
  }, [selectedChallenge, isAuthenticated, currentUser]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem("dashboard_auth");
    localStorage.removeItem("dashboard_role");
    setIsAuthenticated(false);
    setUserRole("");
  };

  
  // Member login/logout functions
  const handleMemberLogin = async (email, password) => {
    try {
      // Normalize email to lowercase for consistent matching
      const normalizedEmail = email.trim().toLowerCase();
      const response = await axios.post(`${API}/auth/login`, { 
        email: normalizedEmail, 
        password 
      });
      const { access_token, user } = response.data;
      
      // Store token and user data
      localStorage.setItem('member_token', access_token);
      localStorage.setItem('current_user', JSON.stringify(user));
      
      setCurrentUser(user);
      setShowMemberLoginDialog(false);
      
      // If user is admin/moderator, also set old auth
      if (user.is_admin || user.is_moderator) {
        setIsAuthenticated(true);
        setUserRole(user.is_admin ? 'admin' : 'moderator');
        localStorage.setItem("dashboard_auth", "true");
        localStorage.setItem("dashboard_role", user.is_admin ? 'admin' : 'moderator');
      }
      
      // Show welcome popup if not hidden
      if (!user.hide_welcome_popup && !user.is_admin) {
        setShowWelcomePopup(true);
        setSelectedTrackForMonth(user.track || "none");
      }
      
      return true;
    } catch (error) {
      console.error("Login error:", error.response?.data || error.message);
      throw error;
    }
  };

  const handleMemberLogout = () => {
    localStorage.removeItem('member_token');
    localStorage.removeItem('current_user');
    setCurrentUser(null);
    handleLogout(); // Also clear admin auth
  };

  // Check for member token on mount
  useEffect(() => {
    const token = localStorage.getItem('member_token');
    const userData = localStorage.getItem('current_user');
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        
        // If user is admin/moderator, set auth state
        if (user.is_admin || user.is_moderator) {
          setIsAuthenticated(true);
          setUserRole(user.is_admin ? 'admin' : 'moderator');
        }
        
        // Check if should show welcome popup
        if (!user.hide_welcome_popup && !user.is_admin) {
          setShowWelcomePopup(true);
          setSelectedTrackForMonth(user.track || "none");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem('member_token');
        localStorage.removeItem('current_user');
      }
    }
  }, []);

  // Fetch tasks by track for submission form
  const fetchTrackTasks = async (challengeId, track) => {
    if (!challengeId) {
      setTrackTasks([]);
      return;
    }
    try {
      // Use track if set, otherwise 'none' - backend handles fallback to general tasks
      const trackParam = (track && track !== 'none') ? track : 'none';
      const response = await axios.get(`${API}/challenges/${challengeId}/tasks-by-track/${trackParam}`);
      const tasks = response.data.tasks || [];
      setTrackTasks(tasks);
      
      // Auto-select first task details if tasks have video/description
      if (tasks.length > 0) {
        const firstTask = tasks.find(t => t.task_number === 1) || tasks[0];
        if (firstTask && (firstTask.video_url || firstTask.description)) {
          setSelectedTaskDetail(firstTask);
        }
      }
    } catch (error) {
      console.error("Error fetching track tasks:", error);
      // No fallback - track tasks must be configured by admin
      setTrackTasks([]);
    }
  };

  // Handle track selection from welcome popup
  const handleTrackSelection = async (track) => {
    if (!currentUser) return;
    
    try {
      await axios.put(`${API}/members/${currentUser.id}/track`, null, {
        params: { track }
      });
      
      // Update local state
      const updatedUser = { ...currentUser, track };
      setCurrentUser(updatedUser);
      localStorage.setItem('current_user', JSON.stringify(updatedUser));
      setSelectedTrackForMonth(track);
    } catch (error) {
      if (error.response?.status === 400) {
        alert(error.response.data.detail);
      } else {
        console.error("Error updating track:", error);
      }
    }
  };

  // Hide welcome popup permanently
  const handleHideWelcomePopup = async () => {
    if (!currentUser) return;
    
    try {
      await axios.put(`${API}/members/${currentUser.id}/hide-welcome`);
      
      // Update local state
      const updatedUser = { ...currentUser, hide_welcome_popup: true };
      setCurrentUser(updatedUser);
      localStorage.setItem('current_user', JSON.stringify(updatedUser));
      setShowWelcomePopup(false);
    } catch (error) {
      console.error("Error hiding welcome popup:", error);
      setShowWelcomePopup(false);
    }
  };

  // Mass import users from CSV
  const handleMassImport = async () => {
    if (!importCsvText.trim()) {
      alert("Please paste CSV data");
      return;
    }
    
    setIsImporting(true);
    setImportResults(null);
    
    try {
      // Parse CSV
      const lines = importCsvText.trim().split('\n');
      const users = [];
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;
        
        // Skip header row if present
        if (i === 0 && (line.toLowerCase().includes('name') || line.toLowerCase().includes('email'))) {
          continue;
        }
        
        const parts = line.split(',').map(p => p.trim().replace(/^["']|["']$/g, ''));
        if (parts.length >= 2) {
          users.push({
            name: parts[0],
            email: parts[1],
            track: parts[2] || 'none'
          });
        }
      }
      
      if (users.length === 0) {
        alert("No valid users found in CSV. Format: name, email, track (optional)");
        setIsImporting(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/members/mass-import`, { users });
      setImportResults(response.data);
      
      // Refresh members list
      fetchData();
    } catch (error) {
      console.error("Error importing users:", error);
      alert("Error importing users: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsImporting(false);
    }
  };


  // Export functionality
  const handleExportImage = async () => {
    try {
      const element = document.getElementById('dashboard-content');
      if (!element) return;
      
      const canvas = await html2canvas(element, {
        backgroundColor: '#f8fafc',
        scale: 2,
        logging: false,
        useCORS: true
      });
      
      // Convert to image and download
      const link = document.createElement('a');
      link.download = `csh-dashboard-${selectedChallenge}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      setShowExportDialog(false);
    } catch (error) {
      console.error("Error exporting image:", error);
      alert("Error exporting dashboard image. Please try again.");
    }
  };

  const handleExportPDF = async () => {
    try {
      const challengeParam = selectedChallenge !== "overall" ? `?challenge_id=${selectedChallenge}` : '';
      const response = await axios.get(`${API}/export/pdf${challengeParam}`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `csh-dashboard-${selectedChallenge}-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowExportDialog(false);
      
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Error exporting PDF. Please try again.");
    }
  };

  const handleExportJSON = async () => {
    try {
      const challengeParam = selectedChallenge !== "overall" ? `?challenge_id=${selectedChallenge}` : '';
      const response = await axios.get(`${API}/export/json${challengeParam}`);
      
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], {type: 'application/json'});
      
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `csh-dashboard-${selectedChallenge}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setShowExportDialog(false);
    } catch (error) {
      console.error("Error exporting JSON:", error);
      alert("Error exporting JSON. Please try again.");
    }
  };

  // Member CRUD operations
  const handleCreateMember = async (e) => {
    e.preventDefault();
    try {
      // Get the auth token
      const token = localStorage.getItem('member_token');
      
      // Use the admin endpoint for creating members with activation
      const response = await axios.post(`${API}/admin/members/create-with-activation`, {
        name: newMember.name,
        email: newMember.email,
        track: newMember.track,
        send_activation_email: newMember.send_activation_email
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // If team was selected, add member to team
      if (newMember.team_id && response.data?.member?.id) {
        try {
          await axios.post(`${API}/admin/teams/${newMember.team_id}/members`, {
            member_id: response.data.member.id
          });
        } catch (teamError) {
          console.error("Error adding member to team:", teamError);
          // Member was created but team assignment failed
        }
      }
      
      setNewMember({ name: "", email: "", track: "none", team_id: "", send_activation_email: true });
      setShowMemberDialog(false);
      fetchData();
      
      if (newMember.send_activation_email) {
        alert(`✅ Member created!${newMember.team_id ? ' Assigned to team.' : ''} Activation email sent to ${newMember.email}`);
      } else {
        alert(`✅ Member created!${newMember.team_id ? ' Assigned to team.' : ''} No activation email sent.`);
      }
      
    } catch (error) {
      console.error("Error creating member:", error);
      alert(error.response?.data?.detail || "Error creating member.");
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const updateData = { name: editingMember.name };
      if (editingMember.email) {
        updateData.email = editingMember.email;
      }
      if (editingMember.track) {
        updateData.track = editingMember.track;
      }
      if (editingMember.membership_type) {
        updateData.membership_type = editingMember.membership_type;
      }
      
      await axios.put(`${API}/members/${editingMember.id}`, updateData);
      setEditingMember(null);
      fetchData();
      
      // If updating current user, refresh their data
      if (currentUser && currentUser.id === editingMember.id) {
        const updatedUser = {...currentUser, ...updateData};
        setCurrentUser(updatedUser);
        localStorage.setItem('current_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error("Error updating member:", error);
      alert("Error updating member.");
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (window.confirm(`Are you sure you want to delete ${memberName}? This will also delete all their submissions.`)) {
      try {
        await axios.delete(`${API}/members/${memberId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting member:", error);
        alert("Error deleting member.");
      }
    }
  };

  // ===== Team CRUD Functions (CSH Story Premier League) =====
  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/teams`, newTeam);
      setShowTeamDialog(false);
      setNewTeam({ name: "" });
      fetchData();
    } catch (error) {
      console.error("Error creating team:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleUpdateTeam = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/teams/${editingTeam.id}`, { name: editingTeam.name });
      setEditingTeam(null);
      fetchData();
    } catch (error) {
      console.error("Error updating team:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (window.confirm(`Are you sure you want to delete team "${teamName}"? All members will be removed from this team.`)) {
      try {
        await axios.delete(`${API}/admin/teams/${teamId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting team:", error);
        alert(`Error: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleAddMemberToTeam = async (teamId, memberId) => {
    try {
      await axios.post(`${API}/admin/teams/${teamId}/members`, { member_id: memberId });
      fetchData();
      if (selectedTeam) {
        fetchTeamDetails(teamId);
      }
      setShowAddMemberToTeamDialog(false);
      setTeamToAddMember(null);
    } catch (error) {
      console.error("Error adding member to team:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleRemoveMemberFromTeam = async (teamId, memberId, memberName) => {
    if (window.confirm(`Remove ${memberName} from this team?`)) {
      try {
        await axios.delete(`${API}/admin/teams/${teamId}/members/${memberId}`);
        fetchData();
        if (selectedTeam) {
          fetchTeamDetails(teamId);
        }
      } catch (error) {
        console.error("Error removing member from team:", error);
        alert(`Error: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleSetTeamCaptain = async (teamId, memberId) => {
    try {
      await axios.put(`${API}/admin/teams/${teamId}/captain`, { member_id: memberId });
      fetchData();
      if (selectedTeam) {
        fetchTeamDetails(teamId);
      }
    } catch (error) {
      console.error("Error setting team captain:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Get members without a team (for adding to teams)
  const getMembersWithoutTeam = () => {
    return members.filter(m => !m.team_id);
  };

  // Get team for a member
  const getMemberTeam = (memberId) => {
    const member = members.find(m => m.id === memberId);
    if (!member || !member.team_id) return null;
    return teams.find(t => t.id === member.team_id);
  };

  // Fetch submissions for a specific member
  const fetchMemberSubmissions = async (memberId) => {
    try {
      const response = await axios.get(`${API}/submissions/member/${memberId}`);
      setMemberSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching member submissions:", error);
      setMemberSubmissions([]);
    }
  };

  const handleViewMemberDetails = (member) => {
    setViewingMember(member);
    fetchMemberSubmissions(member.id);
  };

  // Helper function to get task name
  const getTaskName = (challengeId, taskNumber) => {
    const challenge = challenges.find(c => c.id === challengeId);
    if (!challenge || !challenge.tasks) {
      return `Task ${taskNumber}`;
    }
    const taskIndex = taskNumber - 1; // Convert to 0-based index
    return challenge.tasks[taskIndex] || `Task ${taskNumber}`;
  };

  // Helper functions to get member and challenge names
  const getMemberName = (memberId) => {
    const member = members.find(m => m.id === memberId);
    return member ? member.name : "Unknown Member";
  };

  const getChallengeName = (challengeId) => {
    const challenge = challenges.find(c => c.id === challengeId);
    return challenge ? challenge.title : "Unknown Challenge";
  };

  // Fetch member's challenge-specific stats
  const fetchMemberChallengeStats = async (memberId, challengeId) => {
    if (!memberId || !challengeId) {
      setMemberChallengeStats(null);
      return;
    }
    try {
      const response = await axios.get(`${API}/members/${memberId}/challenge-stats/${challengeId}`);
      setMemberChallengeStats(response.data);
    } catch (error) {
      console.error("Error fetching member challenge stats:", error);
      setMemberChallengeStats(null);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Medal className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-slate-600 font-bold">{rank}</span>;
  };

  // Mobile menu navigation items
  const mainNavItems = [
    { id: "leaderboard", label: "Points Table", icon: Trophy },
    { id: "teams", label: "Teams", icon: Users },
    { id: "progress", label: "Progress", icon: Clock },
    { id: "challenges", label: "Challenges", icon: Target },
    // { id: "feedback-requests", label: "Feedback Hub", icon: MessageSquare }, // Hidden for current challenge
    { id: "members", label: "Players", icon: Users },
    { id: "submissions", label: "Submit Content", icon: Plus }
  ];

  const topMenuItems = [
    {
      id: "public-view",
      label: "Public View",
      icon: Eye,
      component: (
        <Badge variant="outline" className="text-green-600 border-green-300">
          <Eye className="w-4 h-4 mr-1" />
          Public View
        </Badge>
      )
    },
    {
      id: "export",
      label: "Export Dashboard", 
      icon: Download,
      component: (
        <Button 
          onClick={() => {
            setShowExportDialog(true);
            setIsMobileMenuOpen(false);
          }}
          variant="outline"
          className="text-blue-600 border-blue-300 hover:bg-blue-50 w-full justify-start"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Dashboard
        </Button>
      )
    }
  ];

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* PWA Install Banner */}
      {showInstallBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 shadow-lg md:bottom-4 md:left-4 md:right-auto md:w-80 md:rounded-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">Install CSH Blueprint</p>
              <p className="text-white/70 text-xs">Add to home screen for quick access</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={dismissInstallBanner}
                variant="ghost"
                size="sm"
                className="text-white/70 hover:text-white hover:bg-white/10 px-2"
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleInstallApp}
                size="sm"
                className="bg-white text-violet-600 hover:bg-white/90 font-semibold"
              >
                Install
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
      
      {/* Mobile Menu */}
      <div className={`fixed top-0 left-0 h-full w-80 bg-slate-800 shadow-xl transform transition-transform duration-300 ease-in-out z-50 md:hidden ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Menu Header */}
          <div className="p-4 border-b border-orange-500/20 bg-gradient-to-r from-orange-600 to-rose-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-white font-bold">CSH Story Utsav</h2>
                  <p className="text-orange-100 text-sm">Menu</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Top Menu Items */}
          <div className="p-4 border-b border-slate-700 bg-slate-800/50">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Quick Actions</h3>
            <div className="space-y-2">
              {topMenuItems.map((item) => (
                <div key={item.id} className="w-full">
                  {item.component}
                </div>
              ))}
              
              {/* Auth Section */}
              <div className="pt-2 border-t border-slate-700 space-y-2">
                {/* Member Login/Profile */}
                {currentUser ? (
                  <div className="p-2 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 rounded-lg border border-violet-500/20">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm">
                        {currentUser.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{currentUser.name}</p>
                        <p className="text-xs text-amber-400">{currentUser.total_story_points || 0} SP</p>
                      </div>
                    </div>
                    <Button
                      onClick={handleMemberLogout}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-500/20 w-full mt-2 justify-start"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => {
                      setShowMemberLoginDialog(true);
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Member Login
                  </Button>
                )}
                
                {/* Admin Auth */}
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <Badge variant="outline" className="text-blue-400 border-blue-500 bg-blue-500/10 w-full justify-center">
                      {userRole === "admin" ? "Administrator" : "Moderator"}
                    </Badge>
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      size="sm"
                      className="text-red-400 border-red-500 bg-red-500/10 hover:bg-red-500/20 w-full justify-start"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Admin Logout
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 text-center py-1">
                    Admins can login to manage data
                  </p>
                )}
              </div>
              
              {/* WhatsApp Community Link - Only for logged in members */}
              {currentUser && (() => {
                const activeChallenge = challenges.find(c => c.status === 'active');
                const whatsappLink = activeChallenge?.whatsapp_link;
                return whatsappLink ? (
                  <a 
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 mt-2 bg-green-500/20 border border-green-500/30 rounded-xl text-green-300 hover:bg-green-500/30 transition-colors"
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span className="font-medium">Join WhatsApp Community</span>
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </a>
                ) : null;
              })()}
            </div>
          </div>

          {/* Main Navigation */}
          <div className="flex-1 p-4 bg-slate-800">
            {/* Top-Level Views (Mobile) */}
            {currentUser && (
              <div className="space-y-1 mb-4 pb-3 border-b border-slate-700">
                <button
                  onClick={() => { setMainView("my-dashboard"); setIsMobileMenuOpen(false); }}
                  className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                    mainView === 'my-dashboard' ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50' : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Users className="w-5 h-5 mr-3 text-blue-400" />
                  <span className="font-medium">My Dashboard</span>
                </button>
                {currentUser.membership_type === 'diamond' && (
                  <button
                    onClick={() => { setMainView("quest"); setIsMobileMenuOpen(false); }}
                    className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                      mainView === 'quest' ? 'bg-amber-600/30 text-amber-300 border border-amber-500/50' : 'text-amber-300/70 hover:bg-amber-500/10'
                    }`}
                    data-testid="quest-mobile-nav"
                  >
                    <Diamond className="w-5 h-5 mr-3 text-amber-400" />
                    <span className="font-medium">Diamond Quest</span>
                    {mainView !== 'quest' && <span className="ml-auto text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full">Premium</span>}
                  </button>
                )}
                <button
                  onClick={() => { setMainView("community"); setIsMobileMenuOpen(false); }}
                  className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                    mainView === 'community' ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50' : 'text-slate-300 hover:bg-slate-700'
                  }`}
                >
                  <Trophy className="w-5 h-5 mr-3 text-slate-400" />
                  <span className="font-medium">Community Dashboard</span>
                </button>
              </div>
            )}

            {/* Community Tab Navigation */}
            {mainView === 'community' && (
              <>
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Navigation</h3>
            <nav className="space-y-1">
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleTabChange(item.id)}
                    className={`w-full flex items-center px-3 py-3 text-left rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-blue-600/30 text-blue-300 border border-blue-500/50' 
                        : 'text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
                    <span className="font-medium">{item.label}</span>
                    {isActive && (
                      <div className="ml-auto w-2 h-2 bg-blue-400 rounded-full" />
                    )}
                  </button>
                );
              })}
            </nav>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-700 bg-slate-800/50">
            <p className="text-xs text-slate-400 text-center">
              CSH Story Utsav v2.0
            </p>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-r from-slate-800 via-slate-800 to-slate-800 shadow-lg border-b border-orange-500/20 relative overflow-hidden">
        {/* Festive decorative dots */}
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'radial-gradient(circle, #f97316 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
          <div className="flex items-center justify-between">
            {/* Mobile Menu Button */}
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden bg-slate-700 text-slate-200 border-slate-600"
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className={`p-3 rounded-xl shadow-lg ${mainView === 'quest' ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gradient-to-br from-orange-500 to-rose-600'}`}>
                {mainView === 'quest' ? <Diamond className="w-8 h-8 text-amber-300" /> : <Sparkles className="w-8 h-8 text-white" />}
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  {mainView === 'quest' ? 'Diamond Quest' : 'CSH Story Utsav'}
                </h1>
                <p className="text-slate-300 text-sm md:text-base">
                  {mainView === 'quest' ? 'Exclusive challenges for Diamond members' : 'Celebrate storytelling, growth and community'}
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <Badge variant="outline" className="text-green-400 border-green-500 bg-green-500/10">
                <Eye className="w-4 h-4 mr-1" />
                Public View
              </Badge>
              <Button 
                onClick={() => setShowExportDialog(true)}
                variant="outline"
                className="text-blue-400 border-blue-500 bg-blue-500/10 hover:bg-blue-500/20"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Dashboard
              </Button>
              
              {/* Member Login/Profile */}
              {currentUser ? (
                <div className="flex items-center space-x-3">
                  {/* WhatsApp Community Link */}
                  {(() => {
                    const activeChallenge = challenges.find(c => c.status === 'active');
                    const whatsappLink = activeChallenge?.whatsapp_link;
                    return whatsappLink ? (
                      <a 
                        href={whatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-lg text-green-300 hover:bg-green-500/30 transition-colors text-sm"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>WhatsApp</span>
                      </a>
                    ) : null;
                  })()}
                  
                  <div className="flex items-center space-x-2">
                    <MemberAvatar member={currentUser} size="w-8 h-8" textSize="text-xs" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-white">{currentUser.name}</p>
                      <p className="text-xs text-slate-400">{currentUser.total_story_points || 0} SP</p>
                    </div>
                  </div>
                  {currentUser.is_admin && (
                    <Badge variant="outline" className="text-purple-400 border-purple-500 bg-purple-500/10 text-xs">
                      Admin
                    </Badge>
                  )}
                  <Button
                    onClick={handleMemberLogout}
                    variant="outline"
                    size="sm"
                    className="text-red-400 border-red-500 bg-red-500/10 hover:bg-red-500/20"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowMemberLoginDialog(true)}
                  variant="outline"
                  className="text-blue-400 border-blue-500 bg-blue-500/10 hover:bg-blue-500/20"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Member Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Top Level Navigation */}
      {currentUser && (
        <div className="bg-slate-800/50 border-b border-slate-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-2 py-3">
              <Button
                onClick={() => setMainView("my-dashboard")}
                variant={mainView === "my-dashboard" ? "default" : "ghost"}
                className={mainView === "my-dashboard" ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white" : "text-slate-300 hover:text-white hover:bg-slate-700"}
              >
                <Users className="w-4 h-4 mr-2" />
                My Dashboard
              </Button>
              {currentUser?.membership_type === 'diamond' && (
                <Button
                  onClick={() => setMainView("quest")}
                  variant={mainView === "quest" ? "default" : "ghost"}
                  className={mainView === "quest" ? "bg-gradient-to-r from-amber-500 to-orange-600 text-white" : "text-amber-300/70 hover:text-amber-200 hover:bg-amber-500/10"}
                  data-testid="quest-main-nav"
                >
                  <Diamond className="w-4 h-4 mr-2" />
                  Diamond Quest
                </Button>
              )}
              <Button
                onClick={() => setMainView("community")}
                variant={mainView === "community" ? "default" : "ghost"}
                className={mainView === "community" ? "bg-gradient-to-r from-orange-500 to-rose-600 text-white" : "text-slate-300 hover:text-white hover:bg-slate-700"}
              >
                <Trophy className="w-4 h-4 mr-2" />
                Community Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" id="dashboard-content">
        
        {/* Conditional Rendering: My Dashboard vs Diamond Quest vs Community Dashboard */}
        {mainView === "my-dashboard" && currentUser ? (
          /* My Dashboard - Personal View (Already Created in TabsContent) */
          <div className="space-y-6">
            {/* Welcome Header */}
            <Card className="bg-slate-800/80 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                      {currentUser.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-white">Welcome, {currentUser.name}! 👋</h2>
                      <p className="text-slate-300 mt-1">Track your growth journey and Story Points progress</p>
                      {currentUser.track && currentUser.track !== 'none' && (
                        <Badge variant="outline" className="mt-2 capitalize bg-purple-500/20 text-purple-300 border-purple-400">
                          {currentUser.track === 'career' ? '🎯' : currentUser.track === 'business' ? '💼' : '🎓'} {currentUser.track} Track
                        </Badge>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        const activeChallenge = challenges.find(c => c.status === 'active');
                        setNewSubmission({
                          ...newSubmission,
                          member_id: currentUser.id,
                          challenge_id: activeChallenge?.id || ""
                        });
                        if (activeChallenge && currentUser.id) {
                          fetchCompletedTasks(currentUser.id, activeChallenge.id);
                          const userTrack = currentUser?.track || 'none';
                          fetchTrackTasks(activeChallenge.id, userTrack);
                        }
                        setShowSubmissionDialog(true);
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      size="lg"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Submit New Content
                    </Button>
                    {/* Live Attendance Button - Only when live session is active and user hasn't marked yet */}
                    {(() => {
                      const activeChallenge = challenges.find(c => c.status === 'active');
                      if (activeChallenge?.live_session_active) {
                        // Check if user has already marked attendance for this session
                        const sessionStartedAt = activeChallenge.live_session_started_at;
                        const hasMarkedAttendance = sessionStartedAt && submissions.some(s => 
                          s.member_id === currentUser.id && 
                          s.challenge_id === activeChallenge.id &&
                          s.submission_category === 'live_session' &&
                          new Date(s.submission_time) >= new Date(sessionStartedAt)
                        );
                        
                        if (hasMarkedAttendance) {
                          return (
                            <div className="flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <span className="text-green-300 font-medium">Attendance Marked! (+200 SP)</span>
                            </div>
                          );
                        }
                        
                        return (
                          <Button
                            onClick={async () => {
                              try {
                                const response = await axios.post(`${API}/submissions/live-attendance`, {
                                  member_id: currentUser.id,
                                  challenge_id: activeChallenge.id
                                });
                                alert(`Live attendance marked! You earned ${response.data.story_points_awarded} Story Points 🎉`);
                                fetchData();
                              } catch (error) {
                                console.error("Error marking attendance:", error);
                                alert(error.response?.data?.detail || "Error marking attendance. You may have already marked attendance for this session.");
                              }
                            }}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white animate-pulse"
                            size="lg"
                          >
                            <Play className="w-5 h-5 mr-2" />
                            Mark Live Attendance (+200 SP)
                          </Button>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Story Points Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 border-amber-500/30 backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-amber-300 text-sm font-medium">Total Story Points</p>
                      <p className="text-3xl font-bold text-amber-100 mt-1">{currentUser.total_story_points || 0}</p>
                    </div>
                    <Trophy className="w-10 h-10 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className={`backdrop-blur-sm ${currentUser.total_story_points >= 2100 ? 'bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/30' : currentUser.total_story_points >= 1500 ? 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border-yellow-500/30' : 'bg-gradient-to-br from-red-500/20 to-red-600/20 border-red-500/30'}`}>
                <CardContent className="p-4">
                  <div>
                    <p className={`text-sm font-medium ${currentUser.total_story_points >= 2100 ? 'text-green-300' : currentUser.total_story_points >= 1500 ? 'text-yellow-300' : 'text-red-300'}`}>
                      Finisher Status
                    </p>
                    <p className="text-2xl font-bold text-white mt-1">
                      {currentUser.total_story_points >= 2100 ? '🏆 Elite' : currentUser.total_story_points >= 1500 ? '⭐ Monthly' : '🎯 In Progress'}
                    </p>
                    <div className="mt-2">
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${currentUser.total_story_points >= 2100 ? 'bg-green-500' : currentUser.total_story_points >= 1500 ? 'bg-yellow-500' : 'bg-red-500'}`}
                          style={{width: `${Math.min((currentUser.total_story_points / 2100) * 100, 100)}%`}}
                        ></div>
                      </div>
                      <p className="text-xs mt-1 text-slate-400">
                        {currentUser.total_story_points < 1500 && `${1500 - currentUser.total_story_points} SP to Monthly Finisher`}
                        {currentUser.total_story_points >= 1500 && currentUser.total_story_points < 2100 && `${2100 - currentUser.total_story_points} SP to Elite`}
                        {currentUser.total_story_points >= 2100 && 'Elite Status! 🎉'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30 backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-300 text-sm font-medium">Leaderboard Rank</p>
                      <p className="text-3xl font-bold text-blue-100 mt-1">
                        #{leaderboard.findIndex(e => e.member.id === currentUser.id) + 1 || '-'}
                      </p>
                    </div>
                    <Award className="w-10 h-10 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-500/30 backdrop-blur-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-300 text-sm font-medium">Total Submissions</p>
                      <p className="text-3xl font-bold text-purple-100 mt-1">
                        {submissions.filter(s => s.member_id === currentUser.id).length}
                      </p>
                    </div>
                    <FileText className="w-10 h-10 text-purple-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Story Points Breakdown */}
            <Card className="bg-slate-800/80 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <BarChart3 className="w-6 h-6 text-blue-400" />
                  <span>Story Points Breakdown</span>
                </CardTitle>
                <CardDescription className="text-slate-400">Your Story Points by category</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const mySubmissions = submissions.filter(s => s.member_id === currentUser.id);
                  const breakdown = {
                    basic_submission: mySubmissions.filter(s => s.submission_category === 'basic_submission').reduce((sum, s) => sum + (s.story_points_awarded || 0), 0),
                    feedback_request: mySubmissions.filter(s => s.submission_category === 'feedback_request').reduce((sum, s) => sum + (s.story_points_awarded || 0), 0),
                    improvement_evidence: mySubmissions.filter(s => s.submission_category === 'improvement_evidence').reduce((sum, s) => sum + (s.story_points_awarded || 0), 0),
                    feedback_given: mySubmissions.filter(s => s.submission_category === 'feedback_given').reduce((sum, s) => sum + (s.story_points_awarded || 0), 0),
                    live_session: mySubmissions.filter(s => s.submission_category === 'live_session').reduce((sum, s) => sum + (s.story_points_awarded || 0), 0)
                  };
                  
                  const categories = [
                    { name: 'Basic Submissions', value: breakdown.basic_submission, color: 'bg-green-500', icon: '📝' },
                    // { name: 'Feedback Requests', value: breakdown.feedback_request, color: 'bg-blue-500', icon: '💬' }, // Hidden for current challenge
                    // { name: 'Improvement Evidence', value: breakdown.improvement_evidence, color: 'bg-purple-500', icon: '📈' }, // Hidden for current challenge
                    { name: 'Feedback Given', value: breakdown.feedback_given, color: 'bg-amber-500', icon: '🎯' },
                    { name: 'Live Sessions', value: breakdown.live_session, color: 'bg-indigo-500', icon: '🎤' }
                  ];
                  
                  return (
                    <div className="space-y-4">
                      {categories.map((cat) => (
                        <div key={cat.name}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <span>{cat.icon}</span>
                              <span className="text-sm font-medium text-slate-200">{cat.name}</span>
                            </div>
                            <Badge variant="secondary" className="bg-slate-700 text-slate-200">{cat.value} SP</Badge>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-2">
                            <div 
                              className={`${cat.color} h-2 rounded-full transition-all duration-300`}
                              style={{width: `${currentUser.total_story_points > 0 ? (cat.value / currentUser.total_story_points) * 100 : 0}%`}}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Self-Assessment Module */}
            <SelfAssessmentWidget currentUser={currentUser} />

            {/* Feedback Received */}
            <FeedbackReceivedWidget currentUser={currentUser} challenges={challenges} />

            {/* Badges & Achievements Section */}
            <Card className="bg-slate-800/80 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <Medal className="w-6 h-6 text-amber-400" />
                  <span>Badges & Achievements</span>
                </CardTitle>
                <CardDescription className="text-slate-400">Your earned recognition and milestones</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  // Calculate badge eligibility
                  const completedChallenges = challenges.filter(c => {
                    const mySubmissions = submissions.filter(s => s.member_id === currentUser.id && s.challenge_id === c.id);
                    const storyPoints = mySubmissions.reduce((sum, s) => sum + (s.story_points_awarded || 0), 0);
                    return storyPoints >= 1500;
                  }).length;
                  
                  const eliteChallenges = challenges.filter(c => {
                    const mySubmissions = submissions.filter(s => s.member_id === currentUser.id && s.challenge_id === c.id);
                    const storyPoints = mySubmissions.reduce((sum, s) => sum + (s.story_points_awarded || 0), 0);
                    return storyPoints >= 2100;
                  }).length;

                  const badges = [
                    { 
                      type: 'monthly_finisher', 
                      icon: '⭐', 
                      name: 'Monthly Finisher', 
                      desc: 'Complete a challenge with 1500+ SP',
                      earned: completedChallenges > 0,
                      progress: completedChallenges,
                      target: 1,
                      color: 'from-yellow-500/20 to-amber-500/20',
                      border: 'border-yellow-500/30',
                      textColor: 'text-yellow-400'
                    },
                    { 
                      type: 'elite_finisher', 
                      icon: '🏆', 
                      name: 'Elite Finisher', 
                      desc: 'Complete a challenge with 2100+ SP',
                      earned: eliteChallenges > 0,
                      progress: eliteChallenges,
                      target: 1,
                      color: 'from-green-500/20 to-emerald-500/20',
                      border: 'border-green-500/30',
                      textColor: 'text-green-400'
                    },
                    { 
                      type: 'hall_of_fame', 
                      icon: '🎖️', 
                      name: 'Hall of Fame', 
                      desc: 'Complete 3 challenges as finisher',
                      earned: completedChallenges >= 3,
                      progress: completedChallenges,
                      target: 3,
                      color: 'from-purple-500/20 to-fuchsia-500/20',
                      border: 'border-purple-500/30',
                      textColor: 'text-purple-400'
                    },
                    { 
                      type: 'mastery_circle', 
                      icon: '💎', 
                      name: 'Mastery Circle', 
                      desc: 'Complete 12 challenges (1 year)',
                      earned: completedChallenges >= 12,
                      progress: completedChallenges,
                      target: 12,
                      color: 'from-cyan-500/20 to-blue-500/20',
                      border: 'border-cyan-500/30',
                      textColor: 'text-cyan-400'
                    }
                  ];

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {badges.map((badge) => (
                        <div 
                          key={badge.type}
                          className={`relative p-4 rounded-xl border transition-all duration-300 ${
                            badge.earned 
                              ? `bg-gradient-to-br ${badge.color} ${badge.border} shadow-lg` 
                              : 'bg-slate-700/30 border-slate-600/50 opacity-60'
                          }`}
                        >
                          <div className="text-center">
                            <div className={`text-4xl mb-2 ${!badge.earned && 'grayscale'}`}>
                              {badge.icon}
                            </div>
                            <h4 className={`font-semibold text-sm ${badge.earned ? 'text-white' : 'text-slate-400'}`}>
                              {badge.name}
                            </h4>
                            <p className="text-xs text-slate-400 mt-1">{badge.desc}</p>
                            
                            {!badge.earned && (
                              <div className="mt-3">
                                <div className="w-full bg-slate-600/50 rounded-full h-1.5">
                                  <div 
                                    className={`h-1.5 rounded-full ${badge.textColor.replace('text-', 'bg-')}`}
                                    style={{width: `${Math.min((badge.progress / badge.target) * 100, 100)}%`}}
                                  ></div>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                  {badge.progress}/{badge.target}
                                </p>
                              </div>
                            )}
                            
                            {badge.earned && (
                              <div className="absolute -top-1 -right-1">
                                <CheckCircle2 className={`w-5 h-5 ${badge.textColor}`} />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Challenge Progress - Continues below */}
            <Card className="bg-slate-800/80 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <Target className="w-6 h-6 text-indigo-400" />
                  <span>My Challenge Progress</span>
                </CardTitle>
                <CardDescription className="text-slate-400">Current and completed challenges</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {challenges
                    .filter(c => submissions.some(s => s.member_id === currentUser.id && s.challenge_id === c.id))
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                    .map((challenge) => {
                      const mySubmissionsInChallenge = submissions.filter(s => s.member_id === currentUser.id && s.challenge_id === challenge.id);
                      const storyPoints = mySubmissionsInChallenge.reduce((sum, s) => sum + (s.story_points_awarded || 0), 0);
                      const uniqueTasks = new Set(mySubmissionsInChallenge.map(s => s.task_number)).size;
                      const totalTasks = challenge.task_count || 21;
                      const isFinisher = storyPoints >= (challenge.min_story_points || 1500);
                      const isElite = storyPoints >= (challenge.ideal_story_points || 2100);
                      
                      return (
                        <Card key={challenge.id} className={`bg-slate-700/50 backdrop-blur-sm ${isElite ? 'border-l-4 border-l-green-500' : isFinisher ? 'border-l-4 border-l-yellow-500' : 'border-l-4 border-l-slate-500'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h4 className="font-semibold text-white">{challenge.title}</h4>
                                  <Badge variant={challenge.status === 'active' ? 'default' : 'secondary'} className="capitalize bg-blue-500/20 text-blue-300 border-blue-400">
                                    {challenge.status}
                                  </Badge>
                                  {isElite && <span className="text-lg">🏆</span>}
                                  {isFinisher && !isElite && <span className="text-lg">⭐</span>}
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 mb-3">
                                  <div>
                                    <p className="text-xs text-slate-400">Story Points</p>
                                    <p className="text-lg font-bold text-amber-400">{storyPoints} SP</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-400">Prompts Completed</p>
                                    <p className="text-lg font-bold text-blue-400">{uniqueTasks} / {totalTasks}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-400">Submissions</p>
                                    <p className="text-lg font-bold text-purple-400">{mySubmissionsInChallenge.length}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-slate-400">Status</p>
                                    <p className="text-sm font-semibold text-white">
                                      {isElite ? '🏆 Elite' : isFinisher ? '⭐ Monthly' : '🎯 In Progress'}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Progress bar */}
                                <div>
                                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                                    <span>Progress to Elite (2100 SP)</span>
                                    <span>{Math.min(Math.round((storyPoints / 2100) * 100), 100)}%</span>
                                  </div>
                                  <div className="w-full bg-slate-600 rounded-full h-2">
                                    <div 
                                      className={`h-2 rounded-full ${isElite ? 'bg-green-500' : isFinisher ? 'bg-yellow-500' : 'bg-blue-500'}`}
                                      style={{width: `${Math.min((storyPoints / 2100) * 100, 100)}%`}}
                                    ></div>
                                  </div>
                                  {!isFinisher && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      {1500 - storyPoints} more SP needed for Monthly Finisher
                                    </p>
                                  )}
                                  {isFinisher && !isElite && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      {2100 - storyPoints} more SP needed for Elite
                                    </p>
                                  )}
                                </div>
                                
                                {/* View Task Details Button */}
                                <div className="mt-4 pt-4 border-t border-slate-600">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      fetchMemberChallengeProgress(challenge.id, currentUser.id);
                                      setViewingTaskDetails(challenge);
                                    }}
                                    className="w-full border-slate-600 text-slate-200 hover:bg-slate-600"
                                  >
                                    <List className="w-4 h-4 mr-2" />
                                    View Task-by-Task Progress
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  
                  {challenges.filter(c => submissions.some(s => s.member_id === currentUser.id && s.challenge_id === c.id)).length === 0 && (
                    <div className="text-center py-8">
                      <Rocket className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                      <p className="text-slate-400">No challenge participation yet. Start your journey today!</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : mainView === "quest" && currentUser?.membership_type === 'diamond' ? (
          /* Diamond Quest - Separate Premium View */
          <QuestDashboard currentUser={currentUser} challenges={challenges} members={members} />
        ) : (
          /* Community Dashboard - Original Tabs View */
          <div>
        {/* Festive Welcome Banner */}
        <div className="mb-6 relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-600/90 via-rose-600/90 to-fuchsia-600/90 p-5 md:p-6 border border-orange-400/20 shadow-xl">
          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 40%)'}} />
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-yellow-300" />
                The Story Utsav
              </h2>
              <p className="text-orange-100/80 text-sm mt-1">A celebration of storytelling, growth and community spirit</p>
            </div>
            <div className="hidden md:flex items-center gap-3">
              <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
                <p className="text-2xl font-bold text-white">{stats.total_members || 0}</p>
                <p className="text-[10px] text-orange-100/70 uppercase tracking-wide">Storytellers</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/10 rounded-xl backdrop-blur-sm">
                <p className="text-2xl font-bold text-yellow-300">{storyPointsSummary.total_story_points || 0}</p>
                <p className="text-[10px] text-orange-100/70 uppercase tracking-wide">Story Points</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Stats */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-3">
              <Label htmlFor="challenge-selector" className="text-slate-300">View:</Label>
              <Select value={selectedChallenge} onValueChange={setSelectedChallenge}>
                <SelectTrigger className="w-64 bg-slate-800 text-slate-200 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 text-slate-200 border-slate-600">
                  <SelectItem value="overall">Overall Dashboard</SelectItem>
                  {challenges.filter(challenge => challenge.status === 'active' || challenge.status === 'completed').map((challenge) => (
                    <SelectItem key={challenge.id} value={challenge.id}>
                      {challenge.title} ({challenge.status.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="text-purple-300 border-purple-500 bg-purple-500/20 self-start sm:self-center">
              {selectedChallenge === "overall" 
                ? "Showing active & completed challenges" 
                : `Challenge: ${challenges.find(c => c.id === selectedChallenge)?.title || 'Loading...'}`
              }
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {selectedChallenge === "overall" ? (
            <>
              <Card className="bg-gradient-to-br from-orange-500/20 to-amber-600/20 border-orange-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-orange-300 text-sm font-medium">Total Members</p>
                      <p className="text-2xl font-bold text-orange-100 mt-1">{stats.total_members || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-orange-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-rose-500/20 to-pink-600/20 border-rose-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-rose-300 text-sm font-medium">Active Challenges</p>
                      <p className="text-2xl font-bold text-rose-100 mt-1">{stats.total_challenges || 0}</p>
                    </div>
                    <Target className="w-8 h-8 text-rose-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 border-fuchsia-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-fuchsia-300 text-sm font-medium">Total Submissions</p>
                      <p className="text-2xl font-bold text-fuchsia-100 mt-1">{stats.total_submissions || 0}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-fuchsia-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-600/20 border-amber-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-amber-300 text-sm font-medium">Story Points</p>
                      <p className="text-2xl font-bold text-amber-100 mt-1">{storyPointsSummary.total_story_points || 0}</p>
                      <p className="text-xs text-amber-300 mt-1">Avg: {storyPointsSummary.average_story_points || 0} per member</p>
                    </div>
                    <Award className="w-8 h-8 text-amber-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-600/20 border-emerald-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-emerald-300 text-sm font-medium">Monthly Finishers</p>
                      <p className="text-2xl font-bold text-emerald-100 mt-1">{storyPointsSummary.monthly_finishers || 0}</p>
                      <p className="text-xs text-emerald-300 mt-1">Elite: {storyPointsSummary.elite_finishers || 0}</p>
                    </div>
                    <Trophy className="w-8 h-8 text-emerald-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border-blue-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-blue-300 text-sm font-medium">Challenge</p>
                      <p className="text-blue-300 text-sm font-medium">Submissions</p>
                      <p className="text-2xl font-bold text-blue-100 mt-1">{challengeStats.total_submissions || 0}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-blue-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-green-300 text-sm font-medium">Unique</p>
                      <p className="text-green-300 text-sm font-medium">Members</p>
                      <p className="text-2xl font-bold text-green-100 mt-1">{challengeStats.unique_members || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-green-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 border-purple-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-purple-300 text-sm font-medium">Points</p>
                      <p className="text-purple-300 text-sm font-medium">Awarded</p>
                      <p className="text-2xl font-bold text-purple-100 mt-1">{challengeStats.total_points_awarded || 0}</p>
                    </div>
                    <Award className="w-8 h-8 text-purple-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 border-amber-500/30 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-center sm:text-left">
                      <p className="text-amber-300 text-sm font-medium">Avg per</p>
                      <p className="text-amber-300 text-sm font-medium">Member</p>
                      <p className="text-2xl font-bold text-amber-100 mt-1">{challengeStats.avg_submissions_per_member || 0}</p>
                    </div>
                    <BarChart className="w-8 h-8 text-amber-400 mx-auto sm:mx-0 mt-2 sm:mt-0" />
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="hidden md:grid w-full bg-slate-800/80 border border-orange-500/10" style={{gridTemplateColumns: `repeat(${6 + (isAuthenticated || currentUser?.is_admin ? 2 : 0)}, minmax(0, 1fr))`}}>
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">Points Table</TabsTrigger>
            <TabsTrigger value="teams" data-testid="teams-tab" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white text-slate-300">
              <span className="flex items-center gap-1">
                Teams
              </span>
            </TabsTrigger>
            <TabsTrigger value="progress" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">Progress</TabsTrigger>
            <TabsTrigger value="challenges" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">Challenges</TabsTrigger>
            {/* Feedback Hub hidden for current challenge */}
            <TabsTrigger value="members" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">Players</TabsTrigger>
            <TabsTrigger value="submissions" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">Submit Content</TabsTrigger>
            {(isAuthenticated || currentUser?.is_admin) && (
              <TabsTrigger value="approvals" className="relative data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">
                Pending Approvals
                {pendingApprovals.length > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                    {pendingApprovals.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            {(isAuthenticated || currentUser?.is_admin) && (
              <TabsTrigger value="manage-data" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white text-slate-300">
                Manage Data
              </TabsTrigger>
            )}
          </TabsList>

          {/* Mobile Tab Header */}
          <div className="md:hidden mb-4">
            <Card className="bg-slate-800/80 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const currentItem = mainNavItems.find(item => item.id === activeTab);
                      const Icon = currentItem?.icon || Trophy;
                      return (
                        <>
                          <Icon className="w-5 h-5 text-blue-400" />
                          <h2 className="text-lg font-semibold text-white">
                            {currentItem?.label || "Dashboard"}
                          </h2>
                        </>
                      );
                    })()}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMobileMenuOpen(true)}
                    className="text-slate-300 border-slate-600 hover:bg-slate-700"
                  >
                    <Menu className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Leaderboard Tab */}


          <TabsContent value="leaderboard">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">
                  {selectedChallenge === "overall" 
                    ? "Overall Points Table" 
                    : `${challenges.find(c => c.id === selectedChallenge)?.title || "Challenge"} Points Table`
                  }
                </CardTitle>
                <CardDescription className="text-slate-400">
                  {selectedChallenge === "overall" 
                    ? "Top players across all challenges" 
                    : "Rankings for this specific challenge"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {(selectedChallenge === "overall" ? leaderboard : challengeLeaderboard).map((entry) => (
                    <div
                      key={entry.member.id}
                      className={`p-4 rounded-xl border transition-all hover:shadow-lg ${
                        entry.rank <= 3 
                          ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/20 border-amber-500/50' 
                          : 'bg-slate-700/50 border-slate-600 hover:bg-slate-700/70'
                      }`}
                    >
                      {selectedChallenge === "overall" ? (
                        // Overall leaderboard - updated to show Story Points
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            {getRankIcon(entry.rank)}
                            <div className="flex items-center space-x-3">
                              <MemberAvatar member={entry.member} />
                              <div>
                                <div className="flex items-center space-x-2">
                                  <p className="font-semibold text-white">{entry.member.name}</p>
                                  {entry.is_elite_finisher && (
                                    <span title="Elite Finisher (2100+ Story Points)" className="text-lg">🏆</span>
                                  )}
                                  {entry.is_monthly_finisher && !entry.is_elite_finisher && (
                                    <span title="Monthly Finisher (1500+ Story Points)" className="text-lg">⭐</span>
                                  )}
                                </div>
                                {entry.member.track && entry.member.track !== 'none' && (
                                  <p className="text-xs text-slate-400 capitalize">{entry.member.track} Track</p>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="flex flex-col items-end space-y-1">
                              <Badge variant="secondary" className="bg-amber-500/30 text-amber-200 font-bold border-amber-400/50">
                                {entry.total_story_points} SP
                              </Badge>
                              {entry.recent_submissions > 0 && (
                                <Badge variant="outline" className="text-green-400 border-green-500/50 bg-green-500/20 text-xs">
                                  {entry.recent_submissions} recent
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Challenge-specific leaderboard - new two-line layout
                        <div className="space-y-2">
                          {/* First Line: Photo + Name + Points */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              {getRankIcon(entry.rank)}
                              <div className="flex items-center space-x-3">
                                <MemberAvatar member={entry.member} />
                                <div>
                                  <p className="font-semibold text-white">{entry.member.name}</p>
                                </div>
                              </div>
                            </div>
                            <Badge variant="secondary" className="bg-blue-500/30 text-blue-200 border-blue-400/50">
                              {entry.challenge_story_points || entry.challenge_points} points
                            </Badge>
                          </div>
                          
                          {/* Second Line: Submissions Count */}
                          <div className="flex justify-start ml-16">
                            <Badge variant="outline" className="text-purple-400 border-purple-500/50 bg-purple-500/20">
                              Total Submissions - {entry.submissions_count}
                            </Badge>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Teams Tab - CSH Story Premier League */}
          <TabsContent value="teams">
            <TeamsTab
              teamStats={teamStats}
              teamLeaderboard={teamLeaderboard}
              challenges={challenges}
              selectedChallenge={selectedChallenge}
              setSelectedChallenge={setSelectedChallenge}
              isAuthenticated={isAuthenticated}
              currentUser={currentUser}
              setShowTeamDialog={setShowTeamDialog}
              setTeamToAddMember={setTeamToAddMember}
              setShowAddMemberToTeamDialog={setShowAddMemberToTeamDialog}
              setEditingTeam={setEditingTeam}
              handleDeleteTeam={handleDeleteTeam}
              fetchTeamDetails={fetchTeamDetails}
            />
          </TabsContent>

          {/* Progress Tab */}
          <TabsContent value="progress">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Finisher Progress</CardTitle>
                <CardDescription className="text-slate-400">
                  Track member progress towards Monthly Finisher (1500 SP) and Elite Finisher (2100 SP) status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedChallenge === "overall" ? (
                    // Hide progress view for overall dashboard to avoid confusion
                    <div className="text-center py-12 text-slate-400">
                      <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <h3 className="text-lg font-medium mb-2 text-white">Select a Specific Challenge</h3>
                      <p>Choose a challenge from the View dropdown above to see member progress</p>
                      <p className="text-sm mt-2">Progress tracking is available for individual challenges only</p>
                    </div>
                  ) : (() => {
                    // Calculate Story Points per member for the selected challenge
                    const challengeSubmissions = submissions.filter(s => s.challenge_id === selectedChallenge);
                    const memberStoryPoints = {};
                    
                    challengeSubmissions.forEach(sub => {
                      const memberId = sub.member_id;
                      if (!memberStoryPoints[memberId]) {
                        memberStoryPoints[memberId] = 0;
                      }
                      memberStoryPoints[memberId] += (sub.story_points_awarded || 0);
                    });
                    
                    // Get members with their SP
                    const membersWithProgress = Object.entries(memberStoryPoints)
                      .map(([memberId, storyPoints]) => {
                        const member = members.find(m => m.id === memberId);
                        return {
                          member_id: memberId,
                          member_name: member?.name || 'Unknown',
                          story_points: storyPoints,
                          status: storyPoints >= 2100 ? 'elite' : storyPoints >= 1500 ? 'finisher' : 'in-progress'
                        };
                      })
                      .sort((a, b) => b.story_points - a.story_points);
                    
                    if (membersWithProgress.length === 0) {
                      return (
                        <div className="text-center py-8 text-slate-400">
                          <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>No progress data available for this challenge</p>
                          <p className="text-sm">Member progress will appear here once submissions are made</p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid gap-3">
                        {membersWithProgress.map((memberData) => {
                          // Calculate progress percentage (cap at 100%)
                          const progressPercent = Math.min(100, (memberData.story_points / 2100) * 100);
                          const finisherPercent = (1500 / 2100) * 100; // ~71.4%
                          
                          return (
                            <Card 
                              key={memberData.member_id} 
                              className={`border-l-4 bg-slate-700/50 border-slate-600 ${
                                memberData.status === 'elite' ? 'border-l-amber-500' :
                                memberData.status === 'finisher' ? 'border-l-green-500' :
                                'border-l-blue-500'
                              }`}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center space-x-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                                      memberData.status === 'elite' ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                                      memberData.status === 'finisher' ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
                                      'bg-gradient-to-br from-blue-500 to-indigo-600'
                                    }`}>
                                      {memberData.member_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                    </div>
                                    <div>
                                      <h3 className="font-semibold text-white flex items-center gap-2">
                                        {memberData.member_name}
                                        {memberData.status === 'elite' && <span className="text-amber-400">🏆</span>}
                                        {memberData.status === 'finisher' && <span className="text-green-400">⭐</span>}
                                      </h3>
                                      <p className="text-xs text-slate-400">
                                        {memberData.story_points} / 2100 SP
                                        {memberData.status === 'elite' && ' • Elite Finisher!'}
                                        {memberData.status === 'finisher' && ' • Monthly Finisher!'}
                                        {memberData.status === 'in-progress' && ` • ${2100 - memberData.story_points} SP to Elite`}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className={`text-lg font-bold ${
                                      memberData.status === 'elite' ? 'text-amber-400' :
                                      memberData.status === 'finisher' ? 'text-green-400' :
                                      'text-blue-400'
                                    }`}>
                                      {memberData.story_points} SP
                                    </div>
                                  </div>
                                </div>

                                {/* Progress Bar with Milestones */}
                                <div className="relative">
                                  <div className="w-full bg-slate-600 rounded-full h-4 relative overflow-hidden">
                                    {/* Progress Fill */}
                                    <div 
                                      className={`h-4 rounded-full transition-all duration-500 ${
                                        memberData.status === 'elite' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
                                        memberData.status === 'finisher' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                                        'bg-gradient-to-r from-blue-500 to-indigo-500'
                                      }`}
                                      style={{ width: `${progressPercent}%` }}
                                    />
                                    
                                    {/* Finisher Milestone Marker (1500 SP) */}
                                    <div 
                                      className="absolute top-0 bottom-0 w-0.5 bg-green-400"
                                      style={{ left: `${finisherPercent}%` }}
                                      title="Monthly Finisher (1500 SP)"
                                    />
                                    
                                    {/* Elite Marker (2100 SP = 100%) */}
                                    <div 
                                      className="absolute top-0 bottom-0 w-0.5 bg-amber-400 right-0"
                                      title="Elite Finisher (2100 SP)"
                                    />
                                  </div>
                                  
                                  {/* Milestone Labels */}
                                  <div className="flex justify-between mt-1 text-xs text-slate-500">
                                    <span>0</span>
                                    <span style={{ marginLeft: `${finisherPercent - 10}%` }} className="text-green-400">1500 (Finisher)</span>
                                    <span className="text-amber-400">2100 (Elite)</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          {/* Members Tab */}
          <TabsContent value="members">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Community Players</CardTitle>
                <CardDescription className="text-slate-400">
                  Manage community players and their information
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {isAuthenticated && (
                    <div className="flex gap-3">
                      <Button onClick={() => setShowMemberDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Player
                      </Button>
                      <Button onClick={() => setShowMassImportDialog(true)} variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/20">
                        <Upload className="w-4 h-4 mr-2" />
                        Mass Import
                      </Button>
                    </div>
                  )}
                  
                  {/* Membership Filter */}
                  {(isAuthenticated || currentUser?.is_admin) && (
                    <div className="flex items-center gap-2 mb-2">
                      <Label className="text-xs text-slate-400">Filter:</Label>
                      <div className="flex gap-1">
                        {["all", "silver", "diamond"].map(f => (
                          <Button
                            key={f}
                            size="sm"
                            variant={membershipFilter === f ? "default" : "outline"}
                            onClick={() => setMembershipFilter(f)}
                            className={`h-7 text-xs ${membershipFilter === f ? (f === 'diamond' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600') : 'border-slate-600 text-slate-400'}`}
                            data-testid={`filter-${f}`}
                          >
                            {f === 'diamond' && <Diamond className="w-3 h-3 mr-1" />}
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {members
                      .filter(m => membershipFilter === 'all' || (m.membership_type || 'silver') === membershipFilter)
                      .map((member) => (
                      <Card key={member.id} className="hover:shadow-md transition-shadow bg-slate-700/50 border-slate-600">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-3">
                              <MemberAvatar member={member} />
                              <div>
                                <p className="font-semibold text-white flex items-center gap-1">
                                  {member.name}
                                  {member.is_captain && <span title="Team Captain">👑</span>}
                                  {member.membership_type === 'diamond' && <Diamond className="w-3.5 h-3.5 text-amber-400" title="Diamond Member" />}
                                </p>
                                <p className="text-sm text-amber-400">{member.total_story_points || 0} SP</p>
                                {member.team_id && (
                                  <Badge 
                                    className="text-xs bg-amber-900/50 text-amber-300 border border-amber-600/30 mt-1"
                                  >
                                    🏆 {teams.find(t => t.id === member.team_id)?.name || "Team"}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col space-y-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewMemberDetails(member)}
                                className="text-blue-400 border-blue-500/50 hover:bg-blue-500/20"
                                data-testid="view-member-btn"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              {isAuthenticated && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingMember(member)}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-600"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDeleteMember(member.id, member.name)}
                                    className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Add Member Dialog */}
            <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
              <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New Player</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Create a new player account and optionally send an activation email
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateMember} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-slate-200">Full Name *</Label>
                    <Input
                      id="name"
                      value={newMember.name}
                      onChange={(e) => setNewMember({...newMember, name: e.target.value})}
                      required
                      placeholder="Enter member's full name"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-slate-200">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({...newMember, email: e.target.value})}
                      required
                      placeholder="member@example.com"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="track" className="text-slate-200">Track</Label>
                    <Select 
                      value={newMember.track} 
                      onValueChange={(value) => setNewMember({...newMember, track: value})}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select track" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">No Track</SelectItem>
                        <SelectItem value="career">Career Track</SelectItem>
                        <SelectItem value="business">Business Track</SelectItem>
                        <SelectItem value="coach">Coach Track</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Team Assignment Dropdown */}
                  <div>
                    <Label htmlFor="new-team" className="text-slate-200">
                      Assign to Team (Optional)
                      <span className="text-slate-400 font-normal ml-1">- CSH Story Premier League</span>
                    </Label>
                    <Select
                      value={newMember.team_id || ""} 
                      onValueChange={(value) => setNewMember({...newMember, team_id: value === "none" ? "" : value})}
                    >
                      <SelectTrigger id="new-team" className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="No team assignment" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="none">🚫 No Team</SelectItem>
                        {teams.filter(t => (t.member_count || 0) < 8).map(team => (
                          <SelectItem key={team.id} value={team.id}>
                            🏆 {team.name} ({team.member_count || 0}/8 members)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      Only teams with available slots are shown (max 8 members)
                    </p>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-slate-700/50 rounded-lg">
                    <input
                      type="checkbox"
                      id="send_activation"
                      checked={newMember.send_activation_email}
                      onChange={(e) => setNewMember({...newMember, send_activation_email: e.target.checked})}
                      className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500 focus:ring-blue-500"
                    />
                    <div>
                      <Label htmlFor="send_activation" className="text-slate-200 cursor-pointer">
                        Send Activation Email
                      </Label>
                      <p className="text-xs text-slate-400">
                        Member will receive an email with login credentials and activation link
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowMemberDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Player
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Member Dialog */}
            <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
              <DialogContent className="bg-slate-800 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Edit Player</DialogTitle>
                </DialogHeader>
                {editingMember && (
                  <form onSubmit={handleUpdateMember} className="space-y-4">
                    <div>
                      <Label htmlFor="edit-name" className="text-slate-200">Name</Label>
                      <Input
                        id="edit-name"
                        value={editingMember.name}
                        onChange={(e) => setEditingMember({...editingMember, name: e.target.value})}
                        required
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-email" className="text-slate-200">Email</Label>
                      <Input
                        id="edit-email"
                        type="email"
                        value={editingMember.email}
                        onChange={(e) => setEditingMember({...editingMember, email: e.target.value})}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-track" className="text-slate-200">Track (Application Focus)</Label>
                      <Select
                        value={editingMember.track || 'none'}
                        onValueChange={(value) => setEditingMember({...editingMember, track: value})}
                      >
                        <SelectTrigger id="edit-track" className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select track" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="none">No Track Selected</SelectItem>
                          <SelectItem value="career">🎯 Career Confidence</SelectItem>
                          <SelectItem value="business">💼 Business Influence</SelectItem>
                          <SelectItem value="coach">🎓 Authority & Impact</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400 mt-1">
                        Track provides personalized guidance and examples
                      </p>
                    </div>

                    {/* Team Assignment */}
                    <div>
                      <Label htmlFor="edit-team" className="text-slate-200">Team Assignment (CSH Story Premier League)</Label>
                      <Select
                        value={editingMember.team_id || 'none'}
                        onValueChange={async (value) => {
                          const newTeamId = value === 'none' ? null : value;
                          const oldTeamId = editingMember.team_id;
                          
                          // If changing teams, handle the API calls
                          if (oldTeamId !== newTeamId) {
                            try {
                              // Remove from old team if exists
                              if (oldTeamId) {
                                await axios.delete(`${API}/admin/teams/${oldTeamId}/members/${editingMember.id}`);
                              }
                              // Add to new team if selected
                              if (newTeamId) {
                                await axios.post(`${API}/admin/teams/${newTeamId}/members`, {
                                  member_id: editingMember.id
                                });
                              }
                              setEditingMember({...editingMember, team_id: newTeamId});
                              fetchData();
                            } catch (error) {
                              alert('Failed to change team: ' + (error.response?.data?.detail || error.message));
                            }
                          }
                        }}
                      >
                        <SelectTrigger id="edit-team" className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="No team" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="none">🚫 No Team</SelectItem>
                          {teams.map(team => (
                            <SelectItem 
                              key={team.id} 
                              value={team.id}
                              disabled={team.id !== editingMember.team_id && (team.member_count || 0) >= 8}
                            >
                              🏆 {team.name} ({team.member_count || 0}/8)
                              {team.id === editingMember.team_id && " ✓ Current"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Membership Type */}
                    <div>
                      <Label htmlFor="edit-membership" className="text-slate-200">Membership Type</Label>
                      <Select
                        value={editingMember.membership_type || 'silver'}
                        onValueChange={(value) => setEditingMember({...editingMember, membership_type: value})}
                      >
                        <SelectTrigger id="edit-membership" className="bg-slate-700 border-slate-600 text-white" data-testid="edit-membership-select">
                          <SelectValue placeholder="Select membership" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="silver">Silver</SelectItem>
                          <SelectItem value="diamond">
                            <span className="flex items-center gap-1">Diamond</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Admin Password Reset Section */}
                    {(isAuthenticated || currentUser?.is_admin) && (
                      <div className="border-t border-slate-600 pt-4 mt-4">
                        <Label className="text-slate-200 flex items-center gap-2 mb-2">
                          <KeyRound className="w-4 h-4 text-amber-400" />
                          Reset Password
                        </Label>
                        <div className="flex gap-2">
                          <Input
                            type="password"
                            placeholder="New password (min 6 chars)"
                            value={editingMember.newPassword || ''}
                            onChange={(e) => setEditingMember({...editingMember, newPassword: e.target.value})}
                            className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 flex-1"
                            data-testid="admin-reset-password-input"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            disabled={!editingMember.newPassword || editingMember.newPassword.length < 6}
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/admin/members/${editingMember.id}/reset-password`, {
                                  new_password: editingMember.newPassword
                                });
                                alert(`Password reset successfully for ${editingMember.name}`);
                                setEditingMember({...editingMember, newPassword: ''});
                              } catch (error) {
                                alert('Failed to reset password: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                            data-testid="admin-reset-password-btn"
                          >
                            <KeyRound className="w-4 h-4 mr-1" />
                            Reset
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Set a new password for this member
                        </p>
                      </div>
                    )}
                    
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setEditingMember(null)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Update Member</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Member Details Dialog */}
            <Dialog open={!!viewingMember} onOpenChange={(open) => {
              if (!open) {
                setViewingMember(null);
                setMemberChallengeStats(null);
              }
            }}>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-slate-800 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Player Details: {viewingMember?.name}</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    View all submissions and activity for this player
                  </DialogDescription>
                </DialogHeader>
                {viewingMember && (
                  <div className="space-y-6">
                    {/* Member Summary */}
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-4">
                          <MemberAvatar member={viewingMember} size="w-16 h-16" textSize="text-xl" />
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                              {viewingMember.name}
                              {viewingMember.is_captain && (
                                <Badge className="bg-amber-600 text-amber-100">Captain</Badge>
                              )}
                            </h3>
                            {viewingMember.email && (
                              <p className="text-slate-400">{viewingMember.email}</p>
                            )}
                            {/* Team Badge */}
                            {viewingMember.team_id && (
                              <div className="flex items-center gap-2 mt-1">
                                <Badge 
                                  className="bg-amber-900/50 text-amber-300 border border-amber-600/50 cursor-pointer hover:bg-amber-900/70"
                                  onClick={() => {
                                    fetchTeamDetails(viewingMember.team_id);
                                    setViewingMember(null);
                                  }}
                                >
                                  🏆 {teams.find(t => t.id === viewingMember.team_id)?.name || "Team Member"}
                                </Badge>
                              </div>
                            )}
                            <div className="flex items-center space-x-4 mt-2">
                              <div className="flex items-center space-x-1">
                                <Award className="w-4 h-4 text-amber-400" />
                                <span className="font-medium text-amber-400">{viewingMember.total_story_points || 0} Story Points</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Target className="w-4 h-4 text-blue-400" />
                                <span className="font-medium text-blue-400">{memberSubmissions.length} Submissions</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Challenge Points Breakdown */}
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-cyan-300 flex items-center gap-2">
                          <Target className="w-5 h-5" />
                          Challenge Points Breakdown
                        </CardTitle>
                        <div className="mt-2">
                          <Select
                            value={memberChallengeStats?.challenge_id || ""}
                            onValueChange={(value) => {
                              if (value && viewingMember) {
                                fetchMemberChallengeStats(viewingMember.id, value);
                              }
                            }}
                          >
                            <SelectTrigger className="bg-slate-600 border-amber-500/60 text-white w-full hover:border-amber-400 focus:ring-amber-500/30" data-testid="challenge-breakdown-select">
                              <SelectValue placeholder="-- Select a challenge to view breakdown --" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600">
                              {challenges.map(challenge => (
                                <SelectItem key={challenge.id} value={challenge.id} className="text-white hover:bg-slate-600">
                                  {challenge.title}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {memberChallengeStats ? (
                          <div className="space-y-4">
                            {/* Total Points Summary */}
                            <div className="grid grid-cols-3 gap-3">
                              <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-amber-400">{memberChallengeStats.total_story_points}</p>
                                <p className="text-xs text-slate-400">Total SP</p>
                              </div>
                              <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                                <p className="text-2xl font-bold text-blue-400">{memberChallengeStats.total_submissions}</p>
                                <p className="text-xs text-slate-400">Submissions</p>
                              </div>
                              <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                                <Badge className={`${
                                  memberChallengeStats.is_elite ? 'bg-purple-600' :
                                  memberChallengeStats.is_finisher ? 'bg-green-600' :
                                  'bg-slate-600'
                                }`}>
                                  {memberChallengeStats.is_elite ? '🏆 Elite' :
                                   memberChallengeStats.is_finisher ? '✓ Finisher' :
                                   'In Progress'}
                                </Badge>
                                <p className="text-xs text-slate-400 mt-1">
                                  {memberChallengeStats.total_story_points}/{memberChallengeStats.finisher_threshold}
                                </p>
                              </div>
                            </div>
                            
                            {/* Category Breakdown */}
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-slate-300">Points by Category</h4>
                              <div className="space-y-2">
                                {/* Basic Submissions */}
                                <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                                      <Play className="w-4 h-4 text-green-400" />
                                    </div>
                                    <div>
                                      <p className="text-white text-sm">Video Submissions</p>
                                      <p className="text-slate-500 text-xs">{memberChallengeStats.breakdown.basic_submission.count} tasks completed</p>
                                    </div>
                                  </div>
                                  <span className="font-bold text-green-400">+{memberChallengeStats.breakdown.basic_submission.points} SP</span>
                                </div>
                                
                                {/* Feedback Given */}
                                <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                                      <MessageSquare className="w-4 h-4 text-purple-400" />
                                    </div>
                                    <div>
                                      <p className="text-white text-sm">Feedback Given</p>
                                      <p className="text-slate-500 text-xs">{memberChallengeStats.breakdown.feedback_given.count} feedback entries</p>
                                    </div>
                                  </div>
                                  <span className="font-bold text-purple-400">+{memberChallengeStats.breakdown.feedback_given.points} SP</span>
                                </div>
                                
                                {/* Feedback Requested - Hidden for current challenge */}
                                
                                {/* Improvement Evidence - Hidden for current challenge */}
                                
                                {/* Live Sessions */}
                                <div className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                                      <Video className="w-4 h-4 text-indigo-400" />
                                    </div>
                                    <div>
                                      <p className="text-white text-sm">Live Sessions</p>
                                      <p className="text-slate-500 text-xs">{memberChallengeStats.breakdown.live_session.count} attended</p>
                                    </div>
                                  </div>
                                  <span className="font-bold text-indigo-400">+{memberChallengeStats.breakdown.live_session.points} SP</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <p className="text-slate-400 text-center py-4">
                            Select a challenge above to view detailed points breakdown
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Live Sessions Attended */}
                    {(() => {
                      const liveSessions = submissions.filter(s => 
                        s.member_id === viewingMember.id && 
                        s.submission_category === 'live_session'
                      );
                      if (liveSessions.length === 0) return null;
                      
                      return (
                        <Card className="bg-indigo-500/10 border-indigo-500/30">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-indigo-300 flex items-center gap-2">
                              <Play className="w-5 h-5" />
                              Live Sessions Attended ({liveSessions.length})
                            </CardTitle>
                            <CardDescription className="text-slate-400">
                              History of live session attendance • {liveSessions.length * 200} SP earned
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {liveSessions.map((session) => {
                                const challenge = challenges.find(c => c.id === session.challenge_id);
                                return (
                                  <div 
                                    key={session.id} 
                                    className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg border border-slate-600"
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-indigo-500/20 rounded-full flex items-center justify-center">
                                        <Play className="w-5 h-5 text-indigo-400" />
                                      </div>
                                      <div>
                                        <p className="text-white font-medium text-sm">
                                          {challenge?.title || "Challenge"}
                                        </p>
                                        <p className="text-slate-400 text-xs">
                                          {convertUTCToISTDate(session.submission_time)}
                                        </p>
                                      </div>
                                    </div>
                                    <Badge className="bg-indigo-500/30 text-indigo-300">
                                      +{session.story_points_awarded || 200} SP
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })()}

                    {/* Submissions List */}
                    <Card className="bg-slate-700/50 border-slate-600">
                      <CardHeader>
                        <CardTitle className="text-white">All Submissions</CardTitle>
                        <CardDescription className="text-slate-400">
                          Complete history of content submissions by {viewingMember.name}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {memberSubmissions.length > 0 ? (
                          <div className="space-y-4">
                            {memberSubmissions.map((submission) => {
                              const challenge = challenges.find(c => c.id === submission.challenge_id);
                              return (
                                <Card key={submission.id} className="border-l-4 border-l-blue-500 bg-slate-600/50 border-slate-500">
                                  <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <h4 className="font-semibold text-white">
                                          {challenge?.title || "Unknown Challenge"} - {getTaskName(submission.challenge_id, submission.task_number)}
                                        </h4>
                                        <div className="flex items-center space-x-4 mt-2 text-sm text-slate-400">
                                          <div className="flex items-center space-x-1">
                                            <Clock className="w-4 h-4" />
                                            <span>{convertUTCToIST(submission.submission_time)}</span>
                                          </div>
                                          <div className="flex items-center space-x-1">
                                            {submission.submission_type === 'video' && <Video className="w-4 h-4" />}
                                            {submission.submission_type === 'text' && <FileText className="w-4 h-4" />}
                                            {submission.submission_type === 'note' && <StickyNote className="w-4 h-4" />}
                                            <span className="capitalize">{submission.submission_type}</span>
                                          </div>
                                        </div>
                                        <div className="mt-2">
                                          {submission.content_url ? (
                                            <a 
                                              href={submission.content_url} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-blue-400 hover:text-blue-300 text-sm underline"
                                            >
                                              View Content →
                                            </a>
                                          ) : (
                                            <span className="text-slate-500 text-sm italic">
                                              No content URL provided
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="flex items-center space-x-2">
                                          <Badge variant="secondary" className="bg-blue-500/30 text-blue-200">
                                            {submission.points_awarded} pts
                                          </Badge>
                                          {submission.bonus_points > 0 && (
                                            <Badge variant="outline" className="text-green-400 border-green-500/50">
                                              +{submission.bonus_points} bonus
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-slate-400">
                            <Target className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                            <p>No submissions yet</p>
                            <p className="text-sm">This member hasn&apos;t submitted any content.</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Feedback Hub Tab */}
          <TabsContent value="feedback-requests">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-purple-400" />
                  Feedback Hub
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Give feedback on community videos and earn Story Points! You can respond to feedback requests or give feedback on any submission.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Feedback Requests Section */}
                {(() => {
                  const feedbackRequests = submissions.filter(s => 
                    s.submission_category === 'feedback_request' && 
                    s.member_id !== currentUser?.id
                  );
                  
                  if (feedbackRequests.length > 0) {
                    return (
                      <div className="space-y-4 mb-8">
                        <h3 className="text-lg font-semibold text-purple-300 flex items-center gap-2">
                          <HelpCircle className="w-5 h-5" />
                          Feedback Requests ({feedbackRequests.length})
                        </h3>
                        {feedbackRequests.map((request) => {
                          const member = members.find(m => m.id === request.member_id);
                          const challenge = challenges.find(c => c.id === request.challenge_id);
                          const videoId = request.content_url?.includes('youtube.com/watch?v=') 
                            ? request.content_url.split('v=')[1]?.split('&')[0]
                            : request.content_url?.includes('youtu.be/')
                            ? request.content_url.split('youtu.be/')[1]?.split('?')[0]
                            : null;
                          
                          return (
                            <Card key={request.id} className="bg-slate-900/50 border-purple-500/30 overflow-hidden">
                              <div className="grid md:grid-cols-3 gap-4">
                                <div className="md:col-span-1">
                                  {videoId ? (
                                    <div className="aspect-video bg-slate-800 rounded-lg overflow-hidden">
                                      <iframe
                                        src={`https://www.youtube.com/embed/${videoId}`}
                                        title="Feedback Request Video"
                                        className="w-full h-full"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                      />
                                    </div>
                                  ) : (
                                    <div className="aspect-video bg-slate-800 rounded-lg flex items-center justify-center">
                                      <Video className="w-12 h-12 text-slate-600" />
                                    </div>
                                  )}
                                </div>
                                <div className="md:col-span-2 p-4">
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                                          {member?.name?.charAt(0) || '?'}
                                        </div>
                                        <div>
                                          <p className="text-white font-medium">{member?.name || 'Unknown'}</p>
                                          <p className="text-slate-400 text-sm">{challenge?.title || 'Unknown Challenge'}</p>
                                        </div>
                                        <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 text-xs">
                                          Requested Feedback
                                        </Badge>
                                      </div>
                                      {request.feedback_text && (
                                        <div className="bg-slate-800/50 rounded-lg p-3 mt-3 border border-slate-700">
                                          <p className="text-sm text-slate-300 italic">"{request.feedback_text}"</p>
                                        </div>
                                      )}
                                      <p className="text-slate-500 text-xs mt-2">
                                        <Clock className="w-3 h-3 inline mr-1" />
                                        {new Date(request.submission_time).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                      </p>
                                    </div>
                                    {currentUser && (
                                      <div className="flex flex-col gap-2">
                                        <Button
                                          onClick={() => {
                                            setNewSubmission({
                                              ...newSubmission,
                                              member_id: currentUser.id,
                                              challenge_id: request.challenge_id,
                                              submission_category: 'feedback_given',
                                              feedback_to_member_id: request.member_id,
                                              feedback_text: '',
                                              content_url: ''
                                            });
                                            setShowSubmissionDialog(true);
                                          }}
                                          className="bg-purple-600 hover:bg-purple-700 text-white"
                                          data-testid="give-feedback-btn"
                                        >
                                          <MessageSquare className="w-4 h-4 mr-2" />
                                          Give Feedback
                                        </Button>
                                        {(isAuthenticated || currentUser?.is_admin) && (
                                          <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => {
                                              if (window.confirm(`Remove this feedback request from ${member?.name || 'this member'}?`)) {
                                                handleRejectSubmission(request.id, 'Duplicate/removed by admin');
                                              }
                                            }}
                                            data-testid={`remove-feedback-request-${request.id}`}
                                          >
                                            <Trash2 className="w-3 h-3 mr-1" />
                                            Remove
                                          </Button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Community Videos - All submissions from other members */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-cyan-300 flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Community Videos
                    <span className="text-xs text-slate-400 font-normal ml-2">Give feedback on any video to earn 200 SP</span>
                  </h3>
                  {(() => {
                    // Show basic_submission videos from other members (exclude feedback requests already shown above)
                    const communityVideos = submissions.filter(s => 
                      s.submission_category === 'basic_submission' && 
                      s.member_id !== currentUser?.id &&
                      s.content_url  // Only show submissions with a content URL
                    ).sort((a, b) => new Date(b.submission_time) - new Date(a.submission_time))
                    .slice(0, 50); // Limit to recent 50

                    if (communityVideos.length === 0) {
                      return (
                        <div className="text-center py-8 bg-slate-900/50 rounded-lg border border-slate-700">
                          <Video className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                          <p className="text-slate-400">No community videos available yet</p>
                        </div>
                      );
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {communityVideos.map((submission) => {
                          const member = members.find(m => m.id === submission.member_id);
                          const challenge = challenges.find(c => c.id === submission.challenge_id);
                          const videoId = submission.content_url?.includes('youtube.com/watch?v=') 
                            ? submission.content_url.split('v=')[1]?.split('&')[0]
                            : submission.content_url?.includes('youtu.be/')
                            ? submission.content_url.split('youtu.be/')[1]?.split('?')[0]
                            : null;
                          const taskName = challenge?.task_details?.find(t => t.task_number === submission.task_number)?.title || `Task ${submission.task_number}`;
                          
                          return (
                            <Card key={submission.id} className="bg-slate-900/50 border-slate-700 overflow-hidden hover:border-cyan-500/30 transition-colors" data-testid={`community-video-${submission.id}`}>
                              {videoId ? (
                                <div className="aspect-video bg-slate-800">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={`${member?.name || 'Unknown'} - ${taskName}`}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                </div>
                              ) : (
                                <div className="aspect-video bg-slate-800 flex items-center justify-center">
                                  {submission.content_url ? (
                                    <a href={submission.content_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 flex flex-col items-center gap-2">
                                      <ExternalLink className="w-8 h-8" />
                                      <span className="text-sm">View Content</span>
                                    </a>
                                  ) : (
                                    <Video className="w-10 h-10 text-slate-600" />
                                  )}
                                </div>
                              )}
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                      {member?.name?.charAt(0) || '?'}
                                    </div>
                                    <div className="min-w-0">
                                      <p className="text-white text-sm font-medium truncate">{member?.name || 'Unknown'}</p>
                                      <p className="text-slate-400 text-xs truncate">{taskName}</p>
                                    </div>
                                  </div>
                                  {currentUser && (
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        setNewSubmission({
                                          ...newSubmission,
                                          member_id: currentUser.id,
                                          challenge_id: submission.challenge_id,
                                          submission_category: 'feedback_given',
                                          feedback_to_member_id: submission.member_id,
                                          feedback_text: '',
                                          content_url: submission.content_url || ''
                                        });
                                        setShowSubmissionDialog(true);
                                      }}
                                      className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs flex-shrink-0"
                                      data-testid={`give-feedback-video-${submission.id}`}
                                    >
                                      <MessageSquare className="w-3 h-3 mr-1" />
                                      Feedback
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Info Box */}
                <div className="mt-6 bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                  <h4 className="text-purple-300 font-medium flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    How Feedback Works
                  </h4>
                  <ul className="text-slate-400 text-sm mt-2 space-y-1">
                    <li>• Watch any video and provide constructive feedback</li>
                    <li>• Click "Feedback" to submit your response</li>
                    <li>• Earn <span className="text-amber-400">200 SP</span> when your feedback is approved by admin</li>
                    <li>• Quality feedback that helps players improve is highly valued!</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Challenges Management</CardTitle>
                <CardDescription className="text-slate-400">Create and manage challenges</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {(isAuthenticated || currentUser?.is_admin) && (
                    <Button onClick={() => setShowChallengeDialog(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-challenge-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Challenge
                    </Button>
                  )}
                  
                  <div className="space-y-4">
                    {challenges
                      .filter(ch => {
                        // Admins see all challenges
                        if (isAuthenticated || currentUser?.is_admin) return true;
                        // Diamond exclusive challenges hidden from silver members
                        if (ch.visibility === 'diamond_exclusive' && currentUser?.membership_type !== 'diamond') return false;
                        return true;
                      })
                      .map((challenge) => (
                      <Card key={challenge.id} className="p-4 bg-slate-700/50 border-slate-600">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-white">{challenge.title}</h3>
                            <p className="text-slate-400 mt-1">{challenge.description}</p>
                            <div className="flex items-center space-x-3 mt-3">
                              <Badge variant={challenge.status === 'active' ? 'default' : 'secondary'} className={challenge.status === 'active' ? 'bg-green-500/30 text-green-300 border-green-500/50' : 'bg-slate-600 text-slate-300'}>
                                {challenge.status.toUpperCase()}
                              </Badge>
                              {challenge.visibility === 'diamond_exclusive' && (
                                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">
                                  <Diamond className="w-3 h-3 mr-1" />
                                  Diamond
                                </Badge>
                              )}
                              <div className="flex items-center space-x-1">
                                <Award className="w-4 h-4 text-amber-400" />
                                <span className="text-sm font-medium text-slate-300">{challenge.base_points} base points</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Plus className="w-4 h-4 text-green-400" />
                                <span className="text-sm font-medium text-slate-300">{challenge.bonus_points} bonus points</span>
                              </div>
                            </div>
                          </div>
                          {(isAuthenticated || currentUser?.is_admin) && (
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowNotifyDialog(challenge);
                                  setNotifySettings({
                                    prompt_numbers: [],
                                    recipient_type: "all",
                                    track: "none",
                                    selected_member_ids: [],
                                    custom_message: "",
                                    send_immediately: true
                                  });
                                }}
                                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                                data-testid="notify-members-btn"
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingChallenge(challenge)}
                                className="border-slate-600 text-slate-300 hover:bg-slate-600"
                                data-testid="edit-challenge-btn"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete "${challenge.title}"? This will also delete all submissions for this challenge.`)) {
                                    axios.delete(`${API}/challenges/${challenge.id}`)
                                      .then(() => fetchData())
                                      .catch(error => {
                                        console.error("Error deleting challenge:", error);
                                        alert("Error deleting challenge.");
                                      });
                                  }
                                }}
                                className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                                data-testid="delete-challenge-btn"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>

                  {challenges.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      <Target className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                      <p>No challenges yet</p>
                      <p className="text-sm">Create your first challenge to get started.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Add Challenge Dialog */}
            <Dialog open={showChallengeDialog} onOpenChange={setShowChallengeDialog}>
              <DialogContent className="bg-slate-800 border-slate-700 max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Add New Challenge</DialogTitle>
                  <DialogDescription className="text-slate-400">Create a new challenge for the community</DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  axios.post(`${API}/challenges`, newChallenge)
                    .then(() => {
                      setNewChallenge({ 
                        title: "", 
                        description: "", 
                        base_points: 100, 
                        bonus_points: 100, 
                        status: "active",
                        task_count: 21,
                        tasks: Array.from({length: 21}, (_, i) => `Task ${i + 1}`)
                      });
                      setShowChallengeDialog(false);
                      fetchData();
                    })
                    .catch(error => {
                      console.error("Error creating challenge:", error);
                      alert("Error creating challenge.");
                    });
                }} className="space-y-4">
                  <div>
                    <Label htmlFor="title" className="text-slate-200">Challenge Title</Label>
                    <Input
                      id="title"
                      value={newChallenge.title}
                      onChange={(e) => setNewChallenge({...newChallenge, title: e.target.value})}
                      placeholder="Enter challenge title"
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description" className="text-slate-200">Description</Label>
                    <Textarea
                      id="description"
                      value={newChallenge.description}
                      onChange={(e) => setNewChallenge({...newChallenge, description: e.target.value})}
                      placeholder="Describe the challenge objectives and requirements"
                      rows={3}
                      required
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="base_points" className="text-slate-200">Base Points</Label>
                      <Input
                        id="base_points"
                        type="number"
                        min="1"
                        value={newChallenge.base_points}
                        onChange={(e) => setNewChallenge({...newChallenge, base_points: parseInt(e.target.value)})}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-400 mt-1">Points awarded for participation</p>
                    </div>
                    <div>
                      <Label htmlFor="bonus_points" className="text-slate-200">Bonus Points</Label>
                      <Input
                        id="bonus_points"
                        type="number"
                        min="0"
                        value={newChallenge.bonus_points}
                        onChange={(e) => setNewChallenge({...newChallenge, bonus_points: parseInt(e.target.value)})}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-400 mt-1">Additional points for multiple submissions</p>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="status" className="text-slate-200">Status</Label>
                    <Select
                      value={newChallenge.status}
                      onValueChange={(value) => setNewChallenge({...newChallenge, status: value})}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  
                  <div>
                    <Label htmlFor="task_count" className="text-slate-200">Number of Tasks</Label>
                    <Input
                      id="task_count"
                      type="number"
                      min="1"
                      max="50"
                      value={newChallenge.task_count}
                      onChange={(e) => {
                        const newCount = parseInt(e.target.value) || 1;
                        const currentTasks = [...newChallenge.tasks];
                        
                        // Adjust tasks array based on new count
                        if (newCount > currentTasks.length) {
                          // Add more tasks with default names
                          while (currentTasks.length < newCount) {
                            currentTasks.push(`Task ${currentTasks.length + 1}`);
                          }
                        } else if (newCount < currentTasks.length) {
                          // Trim tasks array
                          currentTasks.length = newCount;
                        }
                        
                        setNewChallenge({
                          ...newChallenge, 
                          task_count: newCount,
                          tasks: currentTasks
                        });
                      }}
                      placeholder="Number of tasks (1-50)"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Set the number of tasks for this challenge (1-50)
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="tasks" className="text-slate-200">Task List ({newChallenge.task_count} tasks)</Label>
                    <div className="space-y-2 max-h-60 overflow-y-auto border border-slate-600 rounded p-3 bg-slate-700/50">
                      {newChallenge.tasks.map((task, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <span className="w-8 text-sm font-medium text-slate-400">#{index + 1}</span>
                          <Input
                            value={task}
                            onChange={(e) => {
                              const updatedTasks = [...newChallenge.tasks];
                              updatedTasks[index] = e.target.value;
                              setNewChallenge({...newChallenge, tasks: updatedTasks});
                            }}
                            placeholder={`Task ${index + 1}`}
                            className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Customize the names of all {newChallenge.task_count} tasks for this challenge
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setShowChallengeDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Create Challenge</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Challenge Dialog */}
            <Dialog open={!!editingChallenge} onOpenChange={() => setEditingChallenge(null)}>
              <DialogContent className="bg-slate-800 border-slate-700 max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Edit Challenge</DialogTitle>
                  <DialogDescription className="text-slate-400">Update challenge details</DialogDescription>
                </DialogHeader>
                {editingChallenge && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    axios.put(`${API}/challenges/${editingChallenge.id}`, {
                      title: editingChallenge.title,
                      description: editingChallenge.description,
                      base_points: editingChallenge.base_points,
                      bonus_points: editingChallenge.bonus_points,
                      status: editingChallenge.status,
                      task_count: editingChallenge.task_count,
                      tasks: editingChallenge.tasks,
                      task_details: editingChallenge.task_details || [],
                      tasks_by_track: editingChallenge.tasks_by_track || {},
                      welcome_video_url: editingChallenge.welcome_video_url || null,
                      welcome_message: editingChallenge.welcome_message || null,
                      whatsapp_link: editingChallenge.whatsapp_link || null,
                      live_session_active: editingChallenge.live_session_active || false,
                      visibility: editingChallenge.visibility || 'community'
                    })
                      .then(() => {
                        setEditingChallenge(null);
                        fetchData();
                      })
                      .catch(error => {
                        console.error("Error updating challenge:", error);
                        alert("Error updating challenge.");
                      });
                  }} className="space-y-4">
                    <div>
                      <Label htmlFor="edit-title" className="text-slate-200">Challenge Title</Label>
                      <Input
                        id="edit-title"
                        value={editingChallenge.title}
                        onChange={(e) => setEditingChallenge({...editingChallenge, title: e.target.value})}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-description" className="text-slate-200">Description</Label>
                      <Textarea
                        id="edit-description"
                        value={editingChallenge.description}
                        onChange={(e) => setEditingChallenge({...editingChallenge, description: e.target.value})}
                        rows={3}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-base-points" className="text-slate-200">Base Points</Label>
                        <Input
                          id="edit-base-points"
                          type="number"
                          min="1"
                          value={editingChallenge.base_points}
                          onChange={(e) => setEditingChallenge({...editingChallenge, base_points: parseInt(e.target.value)})}
                          required
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit-bonus-points" className="text-slate-200">Bonus Points</Label>
                        <Input
                          id="edit-bonus-points"
                          type="number"
                          min="0"
                          value={editingChallenge.bonus_points}
                          onChange={(e) => setEditingChallenge({...editingChallenge, bonus_points: parseInt(e.target.value)})}
                          required
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="edit-status" className="text-slate-200">Status</Label>
                      <Select
                        value={editingChallenge.status}
                        onValueChange={(value) => setEditingChallenge({...editingChallenge, status: value})}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Challenge Visibility */}
                    <div>
                      <Label htmlFor="edit-visibility" className="text-slate-200">Challenge Visibility</Label>
                      <Select
                        value={editingChallenge.visibility || 'community'}
                        onValueChange={(value) => setEditingChallenge({...editingChallenge, visibility: value})}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="edit-challenge-visibility">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          <SelectItem value="community">Community (All Members)</SelectItem>
                          <SelectItem value="diamond_exclusive">Diamond Exclusive (Quest Only)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">
                        {editingChallenge.visibility === 'diamond_exclusive' 
                          ? 'Only Diamond members will see this challenge in their Quest tab' 
                          : 'Visible to all Silver and Diamond members'}
                      </p>
                    </div>

                    {/* Welcome Video Configuration */}
                    <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg space-y-3">
                      <Label className="text-violet-300 flex items-center gap-2">
                        <Video className="w-4 h-4" />
                        Welcome Video (shown to members on login)
                      </Label>
                      <Input
                        value={editingChallenge.welcome_video_url || ''}
                        onChange={(e) => setEditingChallenge({...editingChallenge, welcome_video_url: e.target.value})}
                        placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                      <Textarea
                        value={editingChallenge.welcome_message || ''}
                        onChange={(e) => setEditingChallenge({...editingChallenge, welcome_message: e.target.value})}
                        placeholder="Welcome message for this month's challenge..."
                        rows={2}
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>
                    
                    {/* WhatsApp Community Link */}
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
                      <Label className="text-green-300 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        WhatsApp Community Link
                      </Label>
                      <Input
                        value={editingChallenge.whatsapp_link || ''}
                        onChange={(e) => setEditingChallenge({...editingChallenge, whatsapp_link: e.target.value})}
                        placeholder="https://chat.whatsapp.com/YOUR_GROUP_LINK"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                      <p className="text-xs text-slate-400">This link will be shown only to logged-in members</p>
                    </div>
                    
                    {/* Live Session Toggle */}
                    <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-indigo-300 flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          Live Session Active
                        </Label>
                        <button
                          type="button"
                          onClick={() => setEditingChallenge({...editingChallenge, live_session_active: !editingChallenge.live_session_active})}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            editingChallenge.live_session_active ? 'bg-indigo-600' : 'bg-slate-600'
                          }`}
                        >
                          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                            editingChallenge.live_session_active ? 'left-7' : 'left-1'
                          }`} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-400">
                        When enabled, members will see a "Mark Live Attendance" button on their dashboard to earn 200 SP
                      </p>
                      {editingChallenge.live_session_active && (
                        <div className="flex items-center gap-2 text-indigo-300 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Live session is currently active - members can mark attendance
                        </div>
                      )}
                    </div>
                    
                    {/* Track-Specific Tasks Section */}
                    <div className="p-4 bg-gradient-to-r from-blue-500/10 via-green-500/10 to-purple-500/10 border border-slate-600 rounded-lg space-y-4">
                      <div className="flex items-center justify-between">
                        <Label className="text-white flex items-center gap-2 text-lg font-semibold">
                          <Target className="w-5 h-5 text-amber-400" />
                          Track-Specific Tasks
                        </Label>
                        <p className="text-xs text-slate-400">Configure different tasks for each track</p>
                      </div>
                      
                      {/* Track Selector */}
                      <div className="flex gap-2">
                        {[
                          { value: 'career', label: '💼 Career', color: 'blue' },
                          { value: 'business', label: '📈 Business', color: 'green' },
                          { value: 'coach', label: '🎯 Coach', color: 'purple' }
                        ].map((track) => (
                          <Button
                            key={track.value}
                            type="button"
                            onClick={() => {
                              const currentTrackTasks = editingChallenge.tasks_by_track?.[track.value] || [];
                              setEditingChallenge({
                                ...editingChallenge,
                                selectedTrack: track.value,
                                currentTrackTasks: currentTrackTasks
                              });
                            }}
                            className={`flex-1 ${
                              editingChallenge.selectedTrack === track.value
                                ? track.color === 'blue' ? 'bg-blue-600 hover:bg-blue-700' :
                                  track.color === 'green' ? 'bg-green-600 hover:bg-green-700' :
                                  'bg-purple-600 hover:bg-purple-700'
                                : 'bg-slate-700 hover:bg-slate-600'
                            }`}
                          >
                            {track.label}
                            {editingChallenge.tasks_by_track?.[track.value]?.length > 0 && (
                              <Badge className="ml-2 bg-white/20">{editingChallenge.tasks_by_track[track.value].length}</Badge>
                            )}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Track Tasks Editor */}
                      {editingChallenge.selectedTrack && (
                        <div className="space-y-3 mt-4">
                          <div className="flex items-center justify-between">
                            <Label className={`text-sm font-medium ${
                              editingChallenge.selectedTrack === 'career' ? 'text-blue-300' :
                              editingChallenge.selectedTrack === 'business' ? 'text-green-300' :
                              'text-purple-300'
                            }`}>
                              {editingChallenge.selectedTrack === 'career' ? '💼 Career Track Tasks' :
                               editingChallenge.selectedTrack === 'business' ? '📈 Business Track Tasks' :
                               '🎯 Coach Track Tasks'}
                            </Label>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => {
                                const currentTasks = editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [];
                                const newTask = {
                                  task_number: currentTasks.length + 1,
                                  title: `Task ${currentTasks.length + 1}`,
                                  description: '',
                                  video_url: '',
                                  tips: ''
                                };
                                const updatedTasksByTrack = {
                                  ...editingChallenge.tasks_by_track,
                                  [editingChallenge.selectedTrack]: [...currentTasks, newTask]
                                };
                                setEditingChallenge({...editingChallenge, tasks_by_track: updatedTasksByTrack});
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Add Task
                            </Button>
                          </div>
                          
                          <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                            {(editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || []).map((task, index) => (
                              <Card key={index} className={`p-3 ${
                                editingChallenge.selectedTrack === 'career' ? 'bg-blue-500/10 border-blue-500/30' :
                                editingChallenge.selectedTrack === 'business' ? 'bg-green-500/10 border-green-500/30' :
                                'bg-purple-500/10 border-purple-500/30'
                              }`}>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                      editingChallenge.selectedTrack === 'career' ? 'bg-blue-500/30 text-blue-300' :
                                      editingChallenge.selectedTrack === 'business' ? 'bg-green-500/30 text-green-300' :
                                      'bg-purple-500/30 text-purple-300'
                                    }`}>
                                      {index + 1}
                                    </span>
                                    <Input
                                      value={task.title || ''}
                                      onChange={(e) => {
                                        const updatedTasks = [...(editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [])];
                                        updatedTasks[index] = {...updatedTasks[index], title: e.target.value, task_number: index + 1};
                                        setEditingChallenge({
                                          ...editingChallenge,
                                          tasks_by_track: {
                                            ...editingChallenge.tasks_by_track,
                                            [editingChallenge.selectedTrack]: updatedTasks
                                          }
                                        });
                                      }}
                                      placeholder="Task title"
                                      className="flex-1 bg-slate-800/50 border-slate-600 text-white font-medium"
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        const updatedTasks = (editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [])
                                          .filter((_, i) => i !== index)
                                          .map((t, i) => ({...t, task_number: i + 1}));
                                        setEditingChallenge({
                                          ...editingChallenge,
                                          tasks_by_track: {
                                            ...editingChallenge.tasks_by_track,
                                            [editingChallenge.selectedTrack]: updatedTasks
                                          }
                                        });
                                      }}
                                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  
                                  <div className="ml-9 space-y-2">
                                    <Textarea
                                      value={task.description || ''}
                                      onChange={(e) => {
                                        const updatedTasks = [...(editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [])];
                                        updatedTasks[index] = {...updatedTasks[index], description: e.target.value};
                                        setEditingChallenge({
                                          ...editingChallenge,
                                          tasks_by_track: {
                                            ...editingChallenge.tasks_by_track,
                                            [editingChallenge.selectedTrack]: updatedTasks
                                          }
                                        });
                                      }}
                                      placeholder="Task description - what should members do?"
                                      rows={2}
                                      className="bg-slate-800/50 border-slate-600 text-white text-sm placeholder:text-slate-500"
                                    />
                                    
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label className="text-xs text-slate-400 mb-1 block">Video URL (YouTube/Vimeo)</Label>
                                        <Input
                                          value={task.video_url || ''}
                                          onChange={(e) => {
                                            const updatedTasks = [...(editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [])];
                                            updatedTasks[index] = {...updatedTasks[index], video_url: e.target.value};
                                            setEditingChallenge({
                                              ...editingChallenge,
                                              tasks_by_track: {
                                                ...editingChallenge.tasks_by_track,
                                                [editingChallenge.selectedTrack]: updatedTasks
                                              }
                                            });
                                          }}
                                          placeholder="https://youtube.com/watch?v=..."
                                          className="bg-slate-800/50 border-slate-600 text-white text-sm placeholder:text-slate-500"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs text-slate-400 mb-1 block">Tips (optional)</Label>
                                        <Input
                                          value={task.tips || ''}
                                          onChange={(e) => {
                                            const updatedTasks = [...(editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || [])];
                                            updatedTasks[index] = {...updatedTasks[index], tips: e.target.value};
                                            setEditingChallenge({
                                              ...editingChallenge,
                                              tasks_by_track: {
                                                ...editingChallenge.tasks_by_track,
                                                [editingChallenge.selectedTrack]: updatedTasks
                                              }
                                            });
                                          }}
                                          placeholder="Quick tips for members"
                                          className="bg-slate-800/50 border-slate-600 text-white text-sm placeholder:text-slate-500"
                                        />
                                      </div>
                                    </div>
                                    
                                    {task.video_url && (
                                      <div className="text-xs text-emerald-400 flex items-center gap-1">
                                        <Video className="w-3 h-3" />
                                        Video attached
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Card>
                            ))}
                            
                            {(!editingChallenge.tasks_by_track?.[editingChallenge.selectedTrack] || 
                              editingChallenge.tasks_by_track[editingChallenge.selectedTrack].length === 0) && (
                              <div className="text-center py-6 text-slate-400">
                                <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No tasks configured for this track yet.</p>
                                <p className="text-xs mt-1">Click "Add Task" to create track-specific tasks.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {!editingChallenge.selectedTrack && (
                        <div className="text-center py-4 text-slate-400">
                          <p className="text-sm">Select a track above to configure its tasks</p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-task-count" className="text-slate-200">Number of Tasks</Label>
                      <Input
                        id="edit-task-count"
                        type="number"
                        min="1"
                        max="50"
                        value={editingChallenge.task_count || 21}
                        onChange={(e) => {
                          const newCount = parseInt(e.target.value) || 1;
                          const currentTasks = [...(editingChallenge.tasks || Array.from({length: 21}, (_, i) => `Task ${i + 1}`))];
                          
                          // Adjust tasks array based on new count
                          if (newCount > currentTasks.length) {
                            // Add more tasks with default names
                            while (currentTasks.length < newCount) {
                              currentTasks.push(`Task ${currentTasks.length + 1}`);
                            }
                          } else if (newCount < currentTasks.length) {
                            // Trim tasks array
                            currentTasks.length = newCount;
                          }
                          
                          setEditingChallenge({
                            ...editingChallenge, 
                            task_count: newCount,
                            tasks: currentTasks
                          });
                        }}
                        placeholder="Number of tasks (1-50)"
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Set the number of tasks for this challenge (1-50)
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-tasks" className="text-slate-200">Task List with Details ({editingChallenge.task_count || 21} tasks)</Label>
                      <p className="text-xs text-slate-400 mb-2">
                        Add titles, descriptions, and video URLs for each task. Members will see these when viewing the challenge.
                      </p>
                      <div className="space-y-4 max-h-96 overflow-y-auto border border-slate-600 rounded p-3 bg-slate-700/50">
                        {(editingChallenge.tasks || Array.from({length: 21}, (_, i) => `Task ${i + 1}`)).map((task, index) => {
                          const taskDetail = (editingChallenge.task_details || []).find(td => td.task_number === index + 1) || {};
                          return (
                            <Card key={index} className="bg-slate-600/50 border-slate-500 p-3">
                              <div className="space-y-2">
                                <div className="flex items-center space-x-2">
                                  <span className="w-8 h-8 bg-blue-500/30 text-blue-300 rounded-full flex items-center justify-center text-sm font-medium">
                                    {index + 1}
                                  </span>
                                  <Input
                                    value={task}
                                    onChange={(e) => {
                                      const updatedTasks = [...(editingChallenge.tasks || Array.from({length: 21}, (_, i) => `Task ${i + 1}`))];
                                      updatedTasks[index] = e.target.value;
                                      setEditingChallenge({...editingChallenge, tasks: updatedTasks});
                                    }}
                                    placeholder={`Task ${index + 1} title`}
                                    className="flex-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 font-semibold"
                                  />
                                </div>
                                <div className="pl-10 space-y-2">
                                  <div>
                                    <Label className="text-xs text-slate-400">Description (optional)</Label>
                                    <Textarea
                                      value={taskDetail.description || ''}
                                      onChange={(e) => {
                                        const updatedDetails = [...(editingChallenge.task_details || [])];
                                        const existingIdx = updatedDetails.findIndex(td => td.task_number === index + 1);
                                        const newDetail = {
                                          task_number: index + 1,
                                          title: task,
                                          description: e.target.value,
                                          video_url: taskDetail.video_url || '',
                                          tips: taskDetail.tips || ''
                                        };
                                        if (existingIdx >= 0) {
                                          updatedDetails[existingIdx] = newDetail;
                                        } else {
                                          updatedDetails.push(newDetail);
                                        }
                                        setEditingChallenge({...editingChallenge, task_details: updatedDetails});
                                      }}
                                      placeholder="Describe the task requirements..."
                                      rows={2}
                                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-400">Reference Video URL (YouTube/Vimeo)</Label>
                                    <Input
                                      value={taskDetail.video_url || ''}
                                      onChange={(e) => {
                                        const updatedDetails = [...(editingChallenge.task_details || [])];
                                        const existingIdx = updatedDetails.findIndex(td => td.task_number === index + 1);
                                        const newDetail = {
                                          task_number: index + 1,
                                          title: task,
                                          description: taskDetail.description || '',
                                          video_url: e.target.value,
                                          tips: taskDetail.tips || ''
                                        };
                                        if (existingIdx >= 0) {
                                          updatedDetails[existingIdx] = newDetail;
                                        } else {
                                          updatedDetails.push(newDetail);
                                        }
                                        setEditingChallenge({...editingChallenge, task_details: updatedDetails});
                                      }}
                                      placeholder="https://youtube.com/watch?v=..."
                                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-slate-400">Tips (optional)</Label>
                                    <Input
                                      value={taskDetail.tips || ''}
                                      onChange={(e) => {
                                        const updatedDetails = [...(editingChallenge.task_details || [])];
                                        const existingIdx = updatedDetails.findIndex(td => td.task_number === index + 1);
                                        const newDetail = {
                                          task_number: index + 1,
                                          title: task,
                                          description: taskDetail.description || '',
                                          video_url: taskDetail.video_url || '',
                                          tips: e.target.value
                                        };
                                        if (existingIdx >= 0) {
                                          updatedDetails[existingIdx] = newDetail;
                                        } else {
                                          updatedDetails.push(newDetail);
                                        }
                                        setEditingChallenge({...editingChallenge, task_details: updatedDetails});
                                      }}
                                      placeholder="Quick tips for this task..."
                                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setEditingChallenge(null)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Update Challenge</Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Notify Members Dialog */}
            <Dialog open={!!showNotifyDialog} onOpenChange={(open) => !open && setShowNotifyDialog(null)}>
              <DialogContent className="max-w-2xl bg-slate-800 border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <Mail className="w-5 h-5 text-amber-400" />
                    Notify Members About New Prompts
                  </DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Send email notifications to members about new prompts in &quot;{showNotifyDialog?.title}&quot;
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Select Prompts */}
                  <div>
                    <Label className="text-slate-200 mb-2 block">Select Prompts to Notify About</Label>
                    <div className="max-h-40 overflow-y-auto border border-slate-600 rounded-lg p-2 bg-slate-700/50 space-y-1">
                      {Array.from({length: showNotifyDialog?.task_count || 0}, (_, i) => {
                        const promptNum = i + 1;
                        const taskDetail = (showNotifyDialog?.task_details || []).find(t => t.task_number === promptNum);
                        return (
                          <label key={promptNum} className="flex items-center gap-2 p-2 hover:bg-slate-600/50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notifySettings.prompt_numbers.includes(promptNum)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNotifySettings({...notifySettings, prompt_numbers: [...notifySettings.prompt_numbers, promptNum]});
                                } else {
                                  setNotifySettings({...notifySettings, prompt_numbers: notifySettings.prompt_numbers.filter(n => n !== promptNum)});
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-amber-500"
                            />
                            <span className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center text-amber-400 text-sm font-bold">
                              {promptNum}
                            </span>
                            <span className="text-slate-200 text-sm">
                              {taskDetail?.title || `Prompt ${promptNum}`}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setNotifySettings({...notifySettings, prompt_numbers: Array.from({length: showNotifyDialog?.task_count || 0}, (_, i) => i + 1)})}
                        className="border-slate-600 text-slate-300 text-xs"
                      >
                        Select All
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setNotifySettings({...notifySettings, prompt_numbers: []})}
                        className="border-slate-600 text-slate-300 text-xs"
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>
                  
                  {/* Recipients */}
                  <div>
                    <Label className="text-slate-200 mb-2 block">Recipients</Label>
                    <Select 
                      value={notifySettings.recipient_type} 
                      onValueChange={(value) => setNotifySettings({...notifySettings, recipient_type: value})}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="all">All Active Members ({members.filter(m => m.is_active !== false).length})</SelectItem>
                        <SelectItem value="track">By Track</SelectItem>
                        <SelectItem value="selected">Select Individually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Track Selection */}
                  {notifySettings.recipient_type === "track" && (
                    <div>
                      <Label className="text-slate-200 mb-2 block">Select Track</Label>
                      <Select 
                        value={notifySettings.track} 
                        onValueChange={(value) => setNotifySettings({...notifySettings, track: value})}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="career">Career Track ({members.filter(m => m.track === 'career').length})</SelectItem>
                          <SelectItem value="business">Business Track ({members.filter(m => m.track === 'business').length})</SelectItem>
                          <SelectItem value="coach">Coach Track ({members.filter(m => m.track === 'coach').length})</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Individual Selection */}
                  {notifySettings.recipient_type === "selected" && (
                    <div>
                      <Label className="text-slate-200 mb-2 block">Select Members</Label>
                      <div className="max-h-40 overflow-y-auto border border-slate-600 rounded-lg p-2 bg-slate-700/50 space-y-1">
                        {members.filter(m => m.is_active !== false).map(member => (
                          <label key={member.id} className="flex items-center gap-2 p-2 hover:bg-slate-600/50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={notifySettings.selected_member_ids.includes(member.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNotifySettings({...notifySettings, selected_member_ids: [...notifySettings.selected_member_ids, member.id]});
                                } else {
                                  setNotifySettings({...notifySettings, selected_member_ids: notifySettings.selected_member_ids.filter(id => id !== member.id)});
                                }
                              }}
                              className="w-4 h-4 rounded border-slate-500 bg-slate-600 text-blue-500"
                            />
                            <span className="text-slate-200 text-sm">{member.name}</span>
                            <span className="text-slate-400 text-xs">({member.email})</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        {notifySettings.selected_member_ids.length} member(s) selected
                      </p>
                    </div>
                  )}
                  
                  {/* Custom Message */}
                  <div>
                    <Label className="text-slate-200 mb-2 block">Custom Message (Optional)</Label>
                    <textarea
                      value={notifySettings.custom_message}
                      onChange={(e) => setNotifySettings({...notifySettings, custom_message: e.target.value})}
                      placeholder="Add a personal message to include in the email..."
                      className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-3 text-white placeholder:text-slate-400 text-sm resize-none"
                    />
                  </div>
                  
                  {/* Send Options */}
                  <div className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="send_option"
                        checked={notifySettings.send_immediately}
                        onChange={() => setNotifySettings({...notifySettings, send_immediately: true})}
                        className="w-4 h-4 text-amber-500"
                      />
                      <span className="text-slate-200 text-sm">Send Immediately</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="send_option"
                        checked={!notifySettings.send_immediately}
                        onChange={() => setNotifySettings({...notifySettings, send_immediately: false})}
                        className="w-4 h-4 text-blue-500"
                      />
                      <span className="text-slate-200 text-sm">Queue for Daily Digest</span>
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowNotifyDialog(null)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={async () => {
                      if (notifySettings.prompt_numbers.length === 0) {
                        alert("Please select at least one prompt to notify about.");
                        return;
                      }
                      if (notifySettings.recipient_type === "selected" && notifySettings.selected_member_ids.length === 0) {
                        alert("Please select at least one member.");
                        return;
                      }
                      
                      try {
                        const token = localStorage.getItem('member_token');
                        const response = await axios.post(`${API}/admin/notify-prompts`, {
                          challenge_id: showNotifyDialog.id,
                          prompt_numbers: notifySettings.prompt_numbers,
                          recipient_type: notifySettings.recipient_type,
                          track: notifySettings.track,
                          selected_member_ids: notifySettings.selected_member_ids,
                          custom_message: notifySettings.custom_message || null,
                          send_immediately: notifySettings.send_immediately
                        }, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        alert(`✅ ${response.data.message}\n${notifySettings.send_immediately ? `Sent to ${response.data.sent_count} member(s)` : `Queued for ${response.data.queued_recipients} member(s)`}`);
                        setShowNotifyDialog(null);
                      } catch (error) {
                        console.error("Error sending notifications:", error);
                        alert(error.response?.data?.detail || "Error sending notifications.");
                      }
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    {notifySettings.send_immediately ? 'Send Now' : 'Queue for Digest'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Submissions Tab */}
          <TabsContent value="submissions">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white">Submit Content</CardTitle>
                <CardDescription className="text-slate-400">
                  Submit new content for challenges
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!isAuthenticated && !currentUser ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                    <p className="text-slate-400 mb-4">Please log in to submit content</p>
                    <Button 
                      onClick={() => setShowMemberLoginDialog(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Member Login
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <Button 
                      onClick={() => {
                        if (currentUser) {
                          const activeChallenge = challenges.find(c => c.status === 'active');
                          setNewSubmission({
                            ...newSubmission,
                            member_id: currentUser.id,
                            challenge_id: activeChallenge?.id || "",
                            task_number: 1
                          });
                          setCompletedTasks([]);
                          if (activeChallenge) {
                            fetchCompletedTasks(currentUser.id, activeChallenge.id);
                            const userTrack = currentUser?.track || 'none';
                            fetchTrackTasks(activeChallenge.id, userTrack);
                          }
                        }
                        setShowSubmissionDialog(true);
                      }} 
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Submit New Content
                    </Button>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-white">
                          {showAllSubmissions ? 'All Submissions' : (currentUser ? 'My Recent Submissions' : 'Recent Submissions')}
                        </h3>
                        <div className="flex items-center gap-2">
                          {showAllSubmissions && (
                            <Select value={submissionCategoryFilter || "all"} onValueChange={(v) => setSubmissionCategoryFilter(v === "all" ? null : v)}>
                              <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-white h-8 text-xs">
                                <SelectValue placeholder="All Types" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="basic_submission">Video</SelectItem>
                                <SelectItem value="feedback_given">Feedback</SelectItem>
                                <SelectItem value="live_session">Live Session</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {currentUser?.is_admin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAllSubmissions(!showAllSubmissions)}
                              className="border-slate-600 text-slate-300 hover:bg-slate-600"
                            >
                              {showAllSubmissions ? 'Show My Submissions' : 'Manage All Submissions'}
                            </Button>
                          )}
                        </div>
                      </div>
                      {(() => {
                        let filtered = showAllSubmissions && currentUser?.is_admin
                          ? submissions
                          : currentUser 
                            ? submissions.filter(s => s.member_id === currentUser.id)
                          : submissions;
                        // Filter by selected challenge
                        if (showAllSubmissions && selectedChallenge && selectedChallenge !== "overall") {
                          filtered = filtered.filter(s => s.challenge_id === selectedChallenge);
                        }
                        // Filter by category
                        if (showAllSubmissions && submissionCategoryFilter) {
                          filtered = filtered.filter(s => s.submission_category === submissionCategoryFilter);
                        }
                        const display = showAllSubmissions ? filtered : filtered.slice(0, 10);
                        return (
                          <>
                            {showAllSubmissions && (
                              <p className="text-xs text-slate-400 mb-2">
                                Showing {display.length} of {filtered.length} entries
                                {selectedChallenge && selectedChallenge !== "overall" && ` for ${challenges.find(c => c.id === selectedChallenge)?.title || 'selected challenge'}`}
                              </p>
                            )}
                            {display.map((submission) => (
                        <Card key={submission.id} className={`p-4 border-slate-600 ${
                          submission.submission_category === 'feedback_given' 
                            ? 'bg-purple-900/20 border-l-2 border-l-purple-500' 
                            : submission.submission_category === 'live_session'
                            ? 'bg-indigo-900/20 border-l-2 border-l-indigo-500'
                            : 'bg-slate-700/50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-white">{getMemberName(submission.member_id)}</p>
                                <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                                  submission.submission_category === 'feedback_given' 
                                    ? 'text-purple-300 border-purple-500/40' 
                                    : submission.submission_category === 'live_session'
                                    ? 'text-indigo-300 border-indigo-500/40'
                                    : 'text-green-300 border-green-500/40'
                                }`}>
                                  {submission.submission_category === 'feedback_given' ? 'Feedback' 
                                    : submission.submission_category === 'live_session' ? 'Live Session'
                                    : 'Video'}
                                </Badge>
                              </div>
                              <p className="text-sm text-slate-400">{getChallengeName(submission.challenge_id)}</p>
                              {submission.submission_category === 'feedback_given' && submission.feedback_to_member_id && (
                                <p className="text-xs text-purple-300">
                                  Feedback to: {getMemberName(submission.feedback_to_member_id)}
                                </p>
                              )}
                              <p className="text-xs text-slate-500">
                                {convertUTCToISTDate(submission.submission_time)}
                              </p>
                            </div>
                            <div className="text-right flex items-center space-x-2">
                              <div>
                                <Badge variant="secondary" className="bg-amber-500/30 text-amber-200">
                                  {submission.story_points_awarded || 0} SP
                                </Badge>
                              </div>
                              {/* Edit/Delete for own submissions or admin */}
                              {(currentUser?.id === submission.member_id || currentUser?.is_admin) && (
                                <div className="flex space-x-1">
                                  {currentUser?.is_admin && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      title="Edit Points"
                                      onClick={() => {
                                        const currentSP = submission.story_points_awarded || 0;
                                        const newPoints = window.prompt(
                                          `Edit SP for ${getMemberName(submission.member_id)}\nCategory: ${submission.submission_category || 'basic'}\nCurrent: ${currentSP} SP\n\nEnter new points:`,
                                          currentSP
                                        );
                                        if (newPoints !== null) {
                                          const pts = parseInt(newPoints, 10);
                                          if (!isNaN(pts) && pts >= 0) {
                                            axios.put(`${API}/admin/submissions/${submission.id}/update-points`, { story_points: pts })
                                              .then((res) => {
                                                fetchData();
                                                alert(`Updated: ${res.data.message}`);
                                              })
                                              .catch(err => alert(`Error: ${err.response?.data?.detail || err.message}`));
                                          }
                                        }
                                      }}
                                      className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20 h-7 w-7 p-0"
                                      data-testid={`edit-points-quick-${submission.id}`}
                                    >
                                      <Award className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingSubmission({
                                      ...submission,
                                      submission_time: convertUTCToISTForInput(submission.submission_time)
                                    })}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-600 h-7 w-7 p-0"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      if (window.confirm("Are you sure you want to delete this submission?")) {
                                        axios.delete(`${API}/submissions/${submission.id}`)
                                          .then(() => fetchData())
                                          .catch(error => {
                                            console.error("Error deleting submission:", error);
                                            alert("Error deleting submission.");
                                          });
                                      }
                                    }}
                                    className="text-red-400 border-red-500/50 hover:bg-red-500/20 h-7 w-7 p-0"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      ))}
                          </>
                        );
                      })()}
                      {currentUser && submissions.filter(s => s.member_id === currentUser.id).length === 0 && (
                        <div className="text-center py-4 text-slate-400">
                          <p>No submissions yet. Start submitting content to earn Story Points!</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Manage Data Tab */}
          <TabsContent value="manage-data">
            <div className="space-y-6">
              {!isAuthenticated ? (
                <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
                  <CardContent className="text-center py-8">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-slate-500" />
                    <p className="text-slate-400 mb-4">Administrator access required</p>
                    <Button 
                      onClick={() => {
                        setLoginAction("manage data");
                        setShowLoginDialog(true);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Login as Admin
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* All Submissions Management */}
                  <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-white">All Submissions ({submissions.length})</CardTitle>
                      <CardDescription className="text-slate-400">Complete list of all content submissions</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {submissions.map((submission) => (
                          <Card key={submission.id} className="p-4 bg-slate-700/50 border-slate-600">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-white">{getMemberName(submission.member_id)}</p>
                                <p className="text-sm text-slate-400">{getChallengeName(submission.challenge_id)} - {getTaskName(submission.challenge_id, submission.task_number)}</p>
                                <p className="text-xs text-slate-500">
                                {convertUTCToISTDate(submission.submission_time)} • 
                                  <span className="capitalize ml-1">{submission.submission_type}</span>
                                </p>
                                {submission.content_url ? (
                                  <a 
                                    href={submission.content_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-400 hover:text-blue-300 text-xs underline"
                                  >
                                    View Content →
                                  </a>
                                ) : (
                                  <span className="text-slate-500 text-xs italic">
                                    No content URL
                                  </span>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Badge variant="secondary" className="bg-blue-500/30 text-blue-200">
                                    {submission.story_points_awarded || submission.points_awarded} SP
                                  </Badge>
                                  {submission.submission_category && submission.submission_category !== 'basic_submission' && (
                                    <Badge variant="outline" className="text-purple-300 border-purple-500/30 text-xs">
                                      {submission.submission_category.replace('_', ' ')}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex space-x-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    title="Edit Points"
                                    onClick={() => {
                                      const currentSP = submission.story_points_awarded || submission.points_awarded || 0;
                                      const newPoints = window.prompt(
                                        `Edit Story Points for ${getMemberName(submission.member_id)}\nCategory: ${submission.submission_category || 'basic'}\nCurrent: ${currentSP} SP\n\nEnter new points:`,
                                        currentSP
                                      );
                                      if (newPoints !== null) {
                                        const pts = parseInt(newPoints, 10);
                                        if (!isNaN(pts) && pts >= 0) {
                                          axios.put(`${API}/admin/submissions/${submission.id}/update-points`, { story_points: pts })
                                            .then((res) => {
                                              fetchData();
                                              alert(`Updated: ${res.data.message}`);
                                            })
                                            .catch(err => alert(`Error: ${err.response?.data?.detail || err.message}`));
                                        }
                                      }
                                    }}
                                    className="border-amber-500/50 text-amber-300 hover:bg-amber-500/20"
                                    data-testid={`edit-points-${submission.id}`}
                                  >
                                    <Award className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setEditingSubmission({
                                      ...submission,
                                      submission_time: convertUTCToISTForInput(submission.submission_time)
                                    })}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-600"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      if (window.confirm("Are you sure you want to delete this submission?")) {
                                        axios.delete(`${API}/submissions/${submission.id}`)
                                          .then(() => fetchData())
                                          .catch(error => {
                                            console.error("Error deleting submission:", error);
                                            alert("Error deleting submission.");
                                          });
                                      }
                                    }}
                                    className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Edit Submission Dialog */}
            <Dialog open={!!editingSubmission} onOpenChange={() => setEditingSubmission(null)}>
              <DialogContent className="max-w-2xl bg-slate-800 border-slate-700 max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Edit Submission</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Update submission details and points
                  </DialogDescription>
                </DialogHeader>
                {editingSubmission && (
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const submissionData = {
                      member_id: editingSubmission.member_id,
                      challenge_id: editingSubmission.challenge_id,
                      content_url: editingSubmission.content_url,
                      submission_type: editingSubmission.submission_type,
                      custom_submission_time: editingSubmission.submission_time,
                      bonus_points: editingSubmission.bonus_points || 0,
                      timezone: editingSubmission.timezone || "Asia/Kolkata"
                    };
                    
                    axios.put(`${API}/submissions/${editingSubmission.id}`, submissionData)
                      .then(() => {
                        setEditingSubmission(null);
                        fetchData();
                      })
                      .catch(error => {
                        console.error("Error updating submission:", error);
                        alert(`Error updating submission: ${error.response?.data?.detail || error.message}`);
                      });
                  }} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="edit-member" className="text-slate-200">Member</Label>
                        <Select
                          value={editingSubmission.member_id}
                          onValueChange={(value) => setEditingSubmission({...editingSubmission, member_id: value})}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue placeholder="Select member" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            {members.map((member) => (
                              <SelectItem key={member.id} value={member.id}>
                                {member.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="edit-challenge" className="text-slate-200">Challenge</Label>
                        <Select
                          value={editingSubmission.challenge_id}
                          onValueChange={(value) => setEditingSubmission({...editingSubmission, challenge_id: value})}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue placeholder="Select challenge" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            {challenges.map((challenge) => (
                              <SelectItem key={challenge.id} value={challenge.id}>
                                {challenge.title} ({challenge.status.toUpperCase()})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    
                    <div>
                      <Label htmlFor="edit-task-number" className="text-slate-200">Task Number</Label>
                      <Select 
                        value={editingSubmission.task_number?.toString() || "1"} 
                        onValueChange={(value) => setEditingSubmission({...editingSubmission, task_number: parseInt(value)})}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select task number" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-600">
                          {(() => {
                            const selectedChallenge = challenges.find(c => c.id === editingSubmission.challenge_id);
                            const tasks = selectedChallenge?.tasks || Array.from({length: 21}, (_, i) => `Task ${i + 1}`);
                            return tasks.map((taskName, index) => (
                              <SelectItem key={index + 1} value={(index + 1).toString()}>
                                {taskName}
                              </SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="edit-content-url" className="text-slate-200">Content URL (optional)</Label>
                      <Input
                        id="edit-content-url"
                        type="url"
                        value={editingSubmission.content_url}
                        onChange={(e) => setEditingSubmission({...editingSubmission, content_url: e.target.value})}
                        placeholder="https://example.com/content"
                        className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="edit-submission-type" className="text-slate-200">Content Type</Label>
                        <Select
                          value={editingSubmission.submission_type}
                          onValueChange={(value) => setEditingSubmission({...editingSubmission, submission_type: value})}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            <SelectItem value="video">
                              <div className="flex items-center space-x-2">
                                <Video className="w-4 h-4" />
                                <span>Video</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="text">
                              <div className="flex items-center space-x-2">
                                <FileText className="w-4 h-4" />
                                <span>Text Post</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="note">
                              <div className="flex items-center space-x-2">
                                <StickyNote className="w-4 h-4" />
                                <span>Note</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-bonus-points" className="text-slate-200">Bonus Points</Label>
                        <Input
                          id="edit-bonus-points"
                          type="number"
                          min="0"
                          max="1000"
                          value={editingSubmission.bonus_points || 0}
                          onChange={(e) => setEditingSubmission({...editingSubmission, bonus_points: parseInt(e.target.value) || 0})}
                          placeholder="0"
                          className="bg-slate-700 border-slate-600 text-white"
                        />
                        <p className="text-xs text-slate-400 mt-1">Additional points beyond base challenge points</p>
                      </div>
                      
                      <div>
                        <Label htmlFor="edit-timezone" className="text-slate-200">Timezone</Label>
                        <Select
                          value={editingSubmission.timezone || "Asia/Kolkata"}
                          onValueChange={(value) => setEditingSubmission({...editingSubmission, timezone: value})}
                        >
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-600">
                            <SelectItem value="Asia/Kolkata">🇮🇳 IST (India)</SelectItem>
                            <SelectItem value="America/New_York">🇺🇸 EST (New York)</SelectItem>
                            <SelectItem value="America/Los_Angeles">🇺🇸 PST (Los Angeles)</SelectItem>
                            <SelectItem value="Europe/London">🇬🇧 GMT (London)</SelectItem>
                            <SelectItem value="Europe/Berlin">🇩🇪 CET (Berlin)</SelectItem>
                            <SelectItem value="Asia/Tokyo">🇯🇵 JST (Tokyo)</SelectItem>
                            <SelectItem value="Asia/Shanghai">🇨🇳 CST (Shanghai)</SelectItem>
                            <SelectItem value="Australia/Sydney">🇦🇺 AEDT (Sydney)</SelectItem>
                            <SelectItem value="UTC">🌍 UTC (Universal)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="edit-submission-time" className="text-slate-200">Submission Date & Time (IST)</Label>
                      <Input
                        id="edit-submission-time"
                        type="datetime-local"
                        value={editingSubmission.submission_time ? editingSubmission.submission_time.slice(0, 16) : ""}
                        onChange={(e) => setEditingSubmission({...editingSubmission, submission_time: e.target.value})}
                        required
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Time will be interpreted as <strong className="text-slate-300">IST (India Standard Time)</strong>
                      </p>
                    </div>

                    <div className="p-3 bg-amber-500/20 rounded-lg border border-amber-500/30">
                      <div className="flex items-center space-x-2 text-amber-200">
                        <Award className="w-4 h-4" />
                        <span className="text-sm font-medium">Points Summary</span>
                      </div>
                      <p className="text-xs text-amber-300 mt-1">
                        Current points: {editingSubmission.points_awarded || 0} pts 
                        {editingSubmission.bonus_points > 0 && ` (includes ${editingSubmission.bonus_points} bonus)`}
                      </p>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button type="button" variant="outline" onClick={() => setEditingSubmission(null)} className="border-slate-600 text-slate-300 hover:bg-slate-700">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                        Update Submission
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

                  {/* Bulk Import Submissions */}
                  <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Upload className="w-5 h-5 text-green-400" />
                        Bulk Import Submissions
                      </CardTitle>
                      <CardDescription className="text-slate-400">
                        Upload challenge submissions in bulk via CSV format
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300 text-sm">Challenge</Label>
                        <Select value={bulkImportChallenge} onValueChange={setBulkImportChallenge}>
                          <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                            <SelectValue placeholder="Select challenge for import" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            {challenges.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-300 text-sm">
                          CSV Data (one per line: email, task_number, content_url)
                        </Label>
                        <Textarea
                          value={bulkImportData}
                          onChange={(e) => setBulkImportData(e.target.value)}
                          placeholder={"member@email.com, 1, https://youtube.com/...\nmember2@email.com, 2, https://youtube.com/...\nmember3@email.com, 3"}
                          className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 font-mono text-xs"
                          rows={6}
                        />
                        <p className="text-xs text-slate-500">Format: email, task_number, content_url (url optional)</p>
                      </div>
                      <Button
                        disabled={!bulkImportChallenge || !bulkImportData.trim()}
                        onClick={async () => {
                          const lines = bulkImportData.trim().split('\n').filter(l => l.trim());
                          const submissions = lines.map(line => {
                            const parts = line.split(',').map(p => p.trim());
                            return {
                              member_email: parts[0],
                              task_number: parseInt(parts[1]) || 1,
                              content_url: parts[2] || ""
                            };
                          });
                          try {
                            const res = await axios.post(`${API}/admin/submissions/bulk-import`, {
                              challenge_id: bulkImportChallenge,
                              submissions
                            });
                            const d = res.data;
                            alert(`Import complete!\nCreated: ${d.created_count}\nErrors: ${d.error_count}${d.errors?.length ? '\n' + d.errors.map(e => e.entry + ': ' + e.reason).join('\n') : ''}`);
                            setBulkImportData("");
                            fetchData();
                          } catch (err) {
                            alert("Import failed: " + (err.response?.data?.detail || err.message));
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700 w-full"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Import Submissions
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Admin Assessment Management */}
                  <AdminAssessmentManager />

          </TabsContent>


          {/* Pending Approvals Tab */}
          <TabsContent value="approvals">
            <Card className="bg-slate-800/80 border-slate-700 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <Trophy className="w-6 h-6 text-amber-400" />
                  <span>Pending Approvals</span>
                  {pendingApprovals.length > 0 && (
                    <Badge variant="destructive">{pendingApprovals.length} pending</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Review and approve submissions that require admin validation (Improvement Evidence & Feedback Given)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pendingApprovals.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">All Caught Up!</h3>
                    <p className="text-slate-400">No pending approvals at this time</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingApprovals.map((submission) => (
                      <Card key={submission.id} className="border-l-4 border-l-amber-500 bg-slate-700/50 border-slate-600">
                        <CardContent className="p-6">
                          <div className="space-y-4">
                            {/* Header */}
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="text-lg font-semibold text-white">{submission.member_name}</h4>
                                <p className="text-sm text-slate-400">{submission.challenge_name}</p>
                                <div className="flex items-center space-x-2 mt-2">
                                  <Badge variant={submission.submission_category === 'improvement_evidence' ? 'default' : 'secondary'} className="bg-purple-500/30 text-purple-200">
                                    {submission.submission_category === 'improvement_evidence' ? '📈 Improvement Evidence' : '🎯 Feedback Given'}
                                  </Badge>
                                  <Badge variant="outline" className="text-amber-400 border-amber-500/50">
                                    {submission.submission_category === 'improvement_evidence' ? '500' : '200'} SP
                                  </Badge>
                                  <span className="text-xs text-slate-500">{convertUTCToISTDate(submission.submission_time)}</span>
                                </div>
                              </div>
                              <div className="flex space-x-2 items-center">
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => {
                                    const defaultPoints = submission.submission_category === 'improvement_evidence' ? 500 : 200;
                                    const customPoints = window.prompt(
                                      `Approve and award Story Points to ${submission.member_name}.\n\nDefault: ${defaultPoints} SP\nEnter custom points (or leave as-is):`,
                                      defaultPoints
                                    );
                                    if (customPoints !== null) {
                                      const pts = parseInt(customPoints, 10);
                                      if (!isNaN(pts) && pts >= 0) {
                                        handleApproveSubmission(submission.id, undefined, pts);
                                      }
                                    }
                                  }}
                                >
                                  <CheckCircle2 className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    const notes = window.prompt('Rejection reason (optional):');
                                    if (notes !== null) {
                                      handleRejectSubmission(submission.id, notes);
                                    }
                                  }}
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="bg-slate-600/50 rounded-lg p-4 space-y-3">
                              {submission.submission_category === 'improvement_evidence' && (
                                <>
                                  <div>
                                    <Label className="text-xs text-slate-400">Improvement Reflection:</Label>
                                    <p className="text-sm text-white mt-1 whitespace-pre-wrap">
                                      {submission.improvement_reflection || 'No reflection provided'}
                                    </p>
                                  </div>
                                  {submission.first_submission_id && (
                                    <div className="text-xs text-slate-400">
                                      <span className="font-medium">Original Submission ID:</span> {submission.first_submission_id}
                                    </div>
                                  )}
                                  {submission.latest_submission_id && (
                                    <div className="text-xs text-slate-400">
                                      <span className="font-medium">Improved Submission ID:</span> {submission.latest_submission_id}
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {submission.submission_category === 'feedback_given' && (
                                <>
                                  <div>
                                    <Label className="text-xs text-slate-400">Feedback To:</Label>
                                    <p className="text-sm font-medium text-white mt-1">
                                      {members.find(m => m.id === submission.feedback_to_member_id)?.name || 'Unknown Member'}
                                    </p>
                                  </div>
                                  {/* SPARK Feedback Display */}
                                  {submission.spark_improvement ? (
                                    <div className="space-y-2">
                                      <Label className="text-xs text-purple-300 font-medium">SPARK Feedback:</Label>
                                      {submission.spark_specific && (
                                        <div className="flex gap-2 items-start">
                                          <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-xs font-bold flex-shrink-0 mt-0.5">S</span>
                                          <p className="text-sm text-white">{submission.spark_specific}</p>
                                        </div>
                                      )}
                                      {submission.spark_improvement && (
                                        <div className="flex gap-2 items-start">
                                          <span className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold flex-shrink-0 mt-0.5">P</span>
                                          <p className="text-sm text-white">{submission.spark_improvement}</p>
                                        </div>
                                      )}
                                      {submission.spark_actionable && (
                                        <div className="flex gap-2 items-start">
                                          <span className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold flex-shrink-0 mt-0.5">A</span>
                                          <p className="text-sm text-white">{submission.spark_actionable}</p>
                                        </div>
                                      )}
                                      {submission.spark_reflection && (
                                        <div className="flex gap-2 items-start">
                                          <span className="w-5 h-5 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-400 text-xs font-bold flex-shrink-0 mt-0.5">R</span>
                                          <p className="text-sm text-white">{submission.spark_reflection}</p>
                                        </div>
                                      )}
                                      {submission.spark_continue && (
                                        <div className="flex gap-2 items-start">
                                          <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold flex-shrink-0 mt-0.5">K</span>
                                          <p className="text-sm text-white">{submission.spark_continue}</p>
                                        </div>
                                      )}
                                      <p className="text-xs text-slate-500">
                                        {[submission.spark_specific, submission.spark_improvement, submission.spark_actionable, submission.spark_reflection, submission.spark_continue].filter(Boolean).length}/5 fields
                                        {submission.feedback_mode === 'video' && ' | Video feedback'}
                                      </p>
                                    </div>
                                  ) : (
                                    <div>
                                      <Label className="text-xs text-slate-400">Feedback Given:</Label>
                                      <p className="text-sm text-white mt-1 whitespace-pre-wrap">
                                        {submission.feedback_text || 'No feedback text'}
                                      </p>
                                    </div>
                                  )}
                                </>
                              )}
                              
                              {submission.content_url && (
                                <div>
                                  <Label className="text-xs text-slate-400">Content URL:</Label>
                                  <a
                                    href={submission.content_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-400 hover:text-blue-300 flex items-center space-x-1 mt-1"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    <span>View Content</span>
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

      {/* Progress Member Details Dialog */}
      <Dialog 
        open={!!viewingProgressMember} 
        onOpenChange={() => {
          setViewingProgressMember(null);
          setMemberSubmissions([]);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              Member Progress Details: {viewingProgressMember?.member?.member_name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Submissions for {challenges.find(c => c.id === viewingProgressMember?.challengeId)?.name || 'Selected Challenge'}
            </DialogDescription>
          </DialogHeader>
          
          {viewingProgressMember && (() => {
            // When dialog opens, fetch submissions if not already loaded
            if (memberSubmissions.length === 0 && viewingProgressMember.member) {
              fetchMemberSubmissions(viewingProgressMember.member.member_id);
            }
            
            // Filter submissions for the selected challenge
            const challengeSubmissions = memberSubmissions.filter(
              sub => sub.challenge_id === viewingProgressMember.challengeId
            );
            
            return (
              <div className="space-y-6">
                {/* Member Info Card */}
                <Card className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-blue-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-xl">
                        {viewingProgressMember.member.member_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white">{viewingProgressMember.member.member_name}</h3>
                        <div className="flex items-center space-x-4 mt-2 text-sm">
                          <div className="flex items-center space-x-1">
                            <Trophy className="w-4 h-4 text-amber-400" />
                            <span className="font-medium text-amber-400">
                              {viewingProgressMember.member.total_story_points || 0} Story Points
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            <span className="font-medium text-green-400">
                              {challengeSubmissions.length} Submissions in this Challenge
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Submissions Section */}
                <div>
                  <h4 className="text-lg font-semibold mb-3 flex items-center space-x-2 text-white">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <span>Submission History</span>
                  </h4>
                  <p className="text-sm text-slate-400 mb-4">
                    Complete list of submissions by {viewingProgressMember.member.member_name} for this challenge
                  </p>

                  {challengeSubmissions.length > 0 ? (
                    <div className="space-y-3">
                      {challengeSubmissions
                        .sort((a, b) => new Date(b.submission_time) - new Date(a.submission_time))
                        .map((submission) => {
                          const challenge = challenges.find(c => c.id === submission.challenge_id);
                          const taskName = getTaskName(submission.challenge_id, submission.task_number);
                          
                          return (
                            <Card key={submission.id} className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow bg-slate-700/50 border-slate-600">
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/30 text-blue-200">
                                        {taskName}
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/30 text-purple-200">
                                        {submission.content_type}
                                      </span>
                                    </div>
                                    <div className="flex items-center space-x-3 text-sm text-slate-400">
                                      <div className="flex items-center space-x-1">
                                        <Calendar className="w-4 h-4" />
                                        <span>{convertUTCToIST(submission.submission_time)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="flex flex-col items-end space-y-1">
                                      <div className="flex items-center space-x-1">
                                        <Trophy className="w-4 h-4 text-amber-400" />
                                        <span className="font-semibold text-amber-400">
                                          {submission.points_awarded} pts
                                        </span>
                                      </div>
                                      {submission.bonus_points > 0 && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/30 text-green-200">
                                          +{submission.bonus_points} bonus
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                    </div>
                  ) : (
                    <Card className="border-dashed bg-slate-700/50 border-slate-600">
                      <CardContent className="p-8 text-center">
                        <div className="text-slate-500 mb-2">
                          <FileText className="w-12 h-12 mx-auto" />
                        </div>
                        <p className="text-slate-400">No submissions found for this challenge</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

          </div>
        )}
        {/* End conditional rendering */}
      </main>

      {/* Submit Content Dialog - Global dialog that can be opened from anywhere */}
      <Dialog open={showSubmissionDialog} onOpenChange={setShowSubmissionDialog}>
        <DialogContent className="max-w-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700/50 max-h-[85vh] overflow-y-auto shadow-2xl">
          {/* Header with gradient accent */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/10 via-fuchsia-500/10 to-cyan-500/10 rounded-t-lg" />
            <DialogHeader className="relative pb-4 border-b border-slate-700/50">
              <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-xl">
                  <Rocket className="w-6 h-6 text-violet-400" />
                </div>
                Submit Your Content
              </DialogTitle>
              <DialogDescription className="text-slate-400 mt-2">
                Share your progress and earn Story Points
              </DialogDescription>
              
              {/* Current Track Badge */}
              {currentUser?.track && currentUser.track !== 'none' && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-slate-500">Your Track:</span>
                  <Badge className={`${
                    currentUser.track === 'career' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' :
                    currentUser.track === 'business' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                    'bg-purple-500/20 text-purple-300 border-purple-500/30'
                  }`}>
                    {currentUser.track === 'career' ? '💼 Career Confidence' :
                     currentUser.track === 'business' ? '📈 Business Influence' :
                     '🎯 Coach Authority'}
                  </Badge>
                </div>
              )}
            </DialogHeader>
          </div>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            
            // SPARK validation for feedback_given
            if (newSubmission.submission_category === 'feedback_given') {
              const sparkFields = [
                newSubmission.spark_specific,
                newSubmission.spark_improvement,
                newSubmission.spark_actionable,
                newSubmission.spark_reflection,
                newSubmission.spark_continue
              ].filter(f => f && f.trim().length > 0);
              
              if (!newSubmission.spark_improvement?.trim() || !newSubmission.spark_actionable?.trim()) {
                alert("Please include at least one improvement and one actionable suggestion.");
                return;
              }
              if (sparkFields.length < 3) {
                alert("Please fill at least 3 out of 5 SPARK fields for quality feedback.");
                return;
              }
            }
            
            // Only basic_submission requires task selection
            const isTaskBased = newSubmission.submission_category === 'basic_submission';
            
            // Build feedback_text from SPARK fields
            let feedbackText = newSubmission.feedback_text || '';
            if (newSubmission.submission_category === 'feedback_given') {
              const parts = [];
              if (newSubmission.spark_specific?.trim()) parts.push(`[S] ${newSubmission.spark_specific.trim()}`);
              if (newSubmission.spark_improvement?.trim()) parts.push(`[P] ${newSubmission.spark_improvement.trim()}`);
              if (newSubmission.spark_actionable?.trim()) parts.push(`[A] ${newSubmission.spark_actionable.trim()}`);
              if (newSubmission.spark_reflection?.trim()) parts.push(`[R] ${newSubmission.spark_reflection.trim()}`);
              if (newSubmission.spark_continue?.trim()) parts.push(`[K] ${newSubmission.spark_continue.trim()}`);
              feedbackText = parts.join('\n');
            }
            
            const submissionData = {
              member_id: newSubmission.member_id,
              challenge_id: newSubmission.challenge_id,
              task_number: isTaskBased ? newSubmission.task_number : 1,
              content_url: newSubmission.feedback_mode === 'video' ? newSubmission.content_url : (newSubmission.submission_method === 'social_media' ? newSubmission.content_url : null),
              submission_type: newSubmission.submission_type,
              submission_method: newSubmission.submission_method,
              custom_submission_time: newSubmission.submission_time,
              bonus_points: newSubmission.bonus_points || 0,
              timezone: newSubmission.timezone,
              submission_category: newSubmission.submission_category,
              feedback_text: feedbackText || null,
              feedback_to_member_id: newSubmission.feedback_to_member_id || null,
              improvement_reflection: newSubmission.improvement_reflection || null,
              first_submission_id: newSubmission.first_submission_id || null,
              latest_submission_id: newSubmission.latest_submission_id || null,
              submitted_by_admin_id: currentUser?.is_admin && newSubmission.member_id !== currentUser?.id ? currentUser.id : null,
              // SPARK fields
              spark_specific: newSubmission.spark_specific || null,
              spark_improvement: newSubmission.spark_improvement || null,
              spark_actionable: newSubmission.spark_actionable || null,
              spark_reflection: newSubmission.spark_reflection || null,
              spark_continue: newSubmission.spark_continue || null,
              feedback_mode: newSubmission.feedback_mode || "text"
            };
            
            axios.post(`${API}/submissions`, submissionData)
              .then(() => {
                setNewSubmission({ 
                  member_id: currentUser?.id || "", 
                  challenge_id: "", 
                  task_number: 1,
                  content_url: "", 
                  submission_type: "video",
                  submission_method: "social_media",
                  submission_time: getCurrentISTForInput(),
                  bonus_points: 0,
                  timezone: "Asia/Kolkata",
                  submission_category: "basic_submission",
                  feedback_text: "",
                  feedback_to_member_id: "",
                  improvement_reflection: "",
                  first_submission_id: "",
                  latest_submission_id: "",
                  submitted_by_admin_id: "",
                  spark_specific: "",
                  spark_improvement: "",
                  spark_actionable: "",
                  spark_reflection: "",
                  spark_continue: "",
                  feedback_mode: "text"
                });
                setCompletedTasks([]);
                setShowSubmissionDialog(false);
                fetchData();
              })
              .catch(error => {
                console.error("Error creating submission:", error);
                const detail = error.response?.data?.detail;
                let errorMessage = error.message;
                if (detail) {
                  if (Array.isArray(detail)) {
                    // Pydantic validation errors
                    errorMessage = detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n');
                  } else if (typeof detail === 'string') {
                    errorMessage = detail;
                  } else {
                    errorMessage = JSON.stringify(detail);
                  }
                }
                alert(`Error submitting content: ${errorMessage}`);
              });
          }} className="space-y-6 pt-4">
            
            {/* Admin: Member Selection (submit on behalf of others) */}
            {currentUser?.is_admin && (
              <div className="space-y-2 p-3 bg-amber-500/10 rounded-xl border border-amber-500/30">
                <Label className="text-sm font-medium text-amber-300 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Submit On Behalf Of (Admin Only)
                </Label>
                <Select
                  value={newSubmission.member_id}
                  onValueChange={(value) => {
                    setNewSubmission({...newSubmission, member_id: value, challenge_id: "", task_number: 1});
                    setCompletedTasks([]);
                  }}
                >
                  <SelectTrigger className="bg-slate-800/50 border-amber-500/30 text-white h-12 rounded-xl">
                    <SelectValue placeholder="Select member" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} {member.id === currentUser?.id ? "(You)" : ""} - {member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-amber-400/70">
                  As admin, you can submit content on behalf of any member
                </p>
              </div>
            )}

            {/* Challenge Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Target className="w-4 h-4 text-cyan-400" />
                Challenge
              </Label>
              <Select
                value={newSubmission.challenge_id}
                onValueChange={(value) => {
                  setNewSubmission({...newSubmission, challenge_id: value, task_number: 1});
                  // Fetch completed tasks for duplicate prevention
                  if (newSubmission.member_id) {
                    fetchCompletedTasks(newSubmission.member_id, value);
                  }
                  // Always fetch tasks (backend handles track fallback)
                  const userTrack = currentUser?.track || 'none';
                  fetchTrackTasks(value, userTrack);
                  setSelectedTaskDetail(null);
                }}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-600/50 text-white h-12 rounded-xl hover:border-cyan-500/50 transition-colors">
                  <SelectValue placeholder="Select challenge" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {challenges.filter(challenge => challenge.status === 'active').map((challenge) => (
                    <SelectItem key={challenge.id} value={challenge.id}>
                      {challenge.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Submission Category Selection - Comes first to determine if task is needed */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Award className="w-4 h-4 text-violet-400" />
                Submission Type
              </Label>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'basic_submission', icon: '📝', label: 'Basic Submission', desc: 'Regular video/text', points: 100, color: 'from-green-500/20 to-emerald-500/20', border: 'border-green-500/30', active: 'ring-green-500/50' },
                  // { value: 'feedback_request', icon: '💬', label: 'Request Feedback', desc: 'Ask for feedback', points: 100, color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30', active: 'ring-blue-500/50' }, // Hidden for current challenge
                  { value: 'feedback_given', icon: '🎯', label: 'Give Feedback', desc: 'Give feedback to others', points: 200, color: 'from-purple-500/20 to-pink-500/20', border: 'border-purple-500/30', active: 'ring-purple-500/50', requiresApproval: true },
                  // { value: 'improvement_evidence', icon: '📈', label: 'Improvement Evidence', desc: 'Before/after + reflection', points: 500, color: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-500/30', active: 'ring-amber-500/50' } // Hidden for current challenge
                ].map((category) => (
                  <button
                    key={category.value}
                    type="button"
                    onClick={() => setNewSubmission({...newSubmission, submission_category: category.value, feedback_to_member_id: ''})}
                    className={`relative p-4 rounded-xl border transition-all duration-200 text-left
                      ${newSubmission.submission_category === category.value 
                        ? `bg-gradient-to-br ${category.color} ${category.border} ring-2 ${category.active} scale-[1.02]` 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl">{category.icon}</span>
                      <div className="flex items-center gap-1">
                        {category.requiresApproval && (
                          <span className="text-[10px] text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">Needs Approval</span>
                        )}
                        <Badge className={`text-xs ${newSubmission.submission_category === category.value ? 'bg-white/20 text-white' : 'bg-slate-700 text-slate-300'}`}>
                          +{category.points} SP
                        </Badge>
                      </div>
                    </div>
                    <div className="font-medium text-white text-sm">{category.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{category.desc}</div>
                    {newSubmission.submission_category === category.value && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Task Selection - Only for Basic Submission */}
            {newSubmission.submission_category === 'basic_submission' && (
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <List className="w-4 h-4 text-amber-400" />
                Task / Prompt
                {completedTasks.length > 0 && (
                  <span className="text-xs text-green-400 ml-2">
                    ({completedTasks.length} completed)
                  </span>
                )}
              </Label>
              <Select 
                value={newSubmission.task_number.toString()} 
                onValueChange={(value) => {
                  const taskNum = parseInt(value);
                  setNewSubmission({...newSubmission, task_number: taskNum});
                  // Find task details for selected task
                  const taskDetail = trackTasks.find(t => t.task_number === taskNum) || 
                    (challenges.find(c => c.id === newSubmission.challenge_id)?.task_details?.find(t => t.task_number === taskNum));
                  setSelectedTaskDetail(taskDetail || null);
                }}
              >
                <SelectTrigger className="bg-slate-800/50 border-slate-600/50 text-white h-12 rounded-xl hover:border-amber-500/50 transition-colors">
                  <SelectValue placeholder="Select task" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {(() => {
                    // Use completedTasks from API for this member in this challenge
                    const memberCompletedTasks = completedTasks;
                    
                    // Only use track-specific tasks - no fallback to generic tasks
                    if (trackTasks.length > 0) {
                      return trackTasks.map((task) => {
                        const isCompleted = memberCompletedTasks.includes(task.task_number);
                        return (
                          <SelectItem 
                            key={task.task_number} 
                            value={task.task_number.toString()}
                            disabled={isCompleted}
                            className={isCompleted ? 'opacity-50' : ''}
                          >
                            <span className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                                isCompleted 
                                  ? 'bg-green-500/30 text-green-400' 
                                  : 'bg-amber-500/20 text-amber-400'
                              }`}>
                                {isCompleted ? '✓' : task.task_number}
                              </span>
                              <span className={`truncate max-w-[200px] ${isCompleted ? 'line-through text-slate-500' : ''}`}>
                                {task.title || `Task ${task.task_number}`}
                              </span>
                              {task.video_url && <Video className="w-3 h-3 text-blue-400" />}
                              {isCompleted && <span className="text-green-400 text-xs ml-2">Done</span>}
                            </span>
                          </SelectItem>
                        );
                      });
                    } else {
                      // Show message when no track tasks configured
                      return (
                        <div className="p-3 text-center text-slate-400 text-sm">
                          <p>No tasks configured for your track.</p>
                          <p className="text-xs mt-1">Please contact admin to set up tasks.</p>
                        </div>
                      );
                    }
                  })()}
                </SelectContent>
              </Select>
              
              {/* Task Details Preview */}
              {selectedTaskDetail && (
                <div className="mt-3 p-4 bg-slate-800/50 rounded-xl border border-amber-500/20 space-y-3">
                  <h4 className="font-medium text-amber-200 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    {selectedTaskDetail.title || `Task ${selectedTaskDetail.task_number}`}
                  </h4>
                  
                  {selectedTaskDetail.description && (
                    <p className="text-sm text-slate-300">{selectedTaskDetail.description}</p>
                  )}
                  
                  {selectedTaskDetail.video_url && (
                    <div className="aspect-video rounded-lg overflow-hidden bg-slate-900 border border-slate-700">
                      <iframe
                        src={(() => {
                          const url = selectedTaskDetail.video_url;
                          // Handle YouTube URLs
                          if (url.includes('youtube.com/watch')) {
                            // Standard watch URL: youtube.com/watch?v=VIDEO_ID
                            const videoId = url.split('v=')[1]?.split('&')[0];
                            return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
                          } else if (url.includes('youtube.com/embed/')) {
                            // Already embedded URL
                            return url;
                          } else if (url.includes('youtu.be/')) {
                            // Short URL: youtu.be/VIDEO_ID
                            const videoId = url.split('youtu.be/')[1]?.split('?')[0];
                            return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
                          } else if (url.includes('vimeo.com/')) {
                            // Vimeo URL
                            const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
                            return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
                          }
                          return url;
                        })()}
                        title="Task Video"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}
                  
                  {selectedTaskDetail.tips && (
                    <div className="p-2 bg-amber-500/10 rounded-lg">
                      <p className="text-xs text-amber-300">💡 {selectedTaskDetail.tips}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* Improvement Evidence - Previous Submission Selection */}
            {newSubmission.submission_category === 'improvement_evidence' && (
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 border border-purple-500/20 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-purple-300">
                  <TrendingUp className="w-5 h-5" />
                  <span className="font-medium">Show Your Growth</span>
                </div>
                <p className="text-sm text-slate-400">Select your original submission that you&apos;re improving upon:</p>
                
                <Select 
                  value={newSubmission.first_submission_id || ""} 
                  onValueChange={(value) => setNewSubmission({...newSubmission, first_submission_id: value})}
                >
                  <SelectTrigger className="bg-slate-800/50 border-purple-500/30 text-white h-12 rounded-xl hover:border-purple-500/50 transition-colors">
                    <SelectValue placeholder="Select original submission" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                    {submissions
                      .filter(s => s.member_id === (currentUser?.id || newSubmission.member_id) && s.submission_category === 'basic_submission')
                      .map((sub) => (
                        <SelectItem key={sub.id} value={sub.id}>
                          <span className="flex items-center gap-2">
                            <span className="text-slate-400">Task {sub.task_number}</span>
                            <span className="text-white">{new Date(sub.submission_time).toLocaleDateString()}</span>
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <div className="space-y-2">
                  <Label className="text-sm font-medium text-purple-300">Reflection (What did you improve?)</Label>
                  <textarea
                    value={newSubmission.improvement_reflection || ''}
                    onChange={(e) => setNewSubmission({...newSubmission, improvement_reflection: e.target.value})}
                    placeholder="Describe what you learned and how you improved..."
                    className="w-full h-24 bg-slate-900/50 border border-purple-500/20 text-white placeholder:text-slate-500 rounded-lg p-3 focus:border-purple-500/50 focus:ring-purple-500/20 focus:outline-none resize-none"
                  />
                </div>
              </div>
            )}

            {/* Feedback Request - What feedback do you need? */}
            {newSubmission.submission_category === 'feedback_request' && (
              <div className="p-4 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-blue-300">
                  <MessageSquare className="w-5 h-5" />
                  <span className="font-medium">Request Feedback</span>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-blue-300">What feedback are you looking for?</Label>
                  <textarea
                    value={newSubmission.feedback_text || ''}
                    onChange={(e) => setNewSubmission({...newSubmission, feedback_text: e.target.value})}
                    placeholder="e.g., Please review my opening hook, body language, or closing..."
                    className="w-full h-20 bg-slate-900/50 border border-blue-500/20 text-white placeholder:text-slate-500 rounded-lg p-3 focus:border-blue-500/50 focus:ring-blue-500/20 focus:outline-none resize-none"
                  />
                </div>
              </div>
            )}

            {/* Feedback Given - Browse current challenge videos */}
            {newSubmission.submission_category === 'feedback_given' && (
              <div className="p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-purple-300">
                  <MessageSquare className="w-5 h-5" />
                  <span className="font-medium">Give Feedback on a Video</span>
                </div>
                
                {/* Video Selection for Feedback - current challenge only, per-video tracking */}
                <div className="space-y-3 max-h-72 overflow-y-auto">
                  <Label className="text-sm font-medium text-purple-300">Select a video to give feedback on:</Label>
                  {(() => {
                    const activeChallengeId = newSubmission.challenge_id || challenges.find(c => c.status === 'active')?.id;
                    
                    // Get basic_submission entries from the active challenge only (exclude current user)
                    const challengeVideos = submissions.filter(s =>
                      s.submission_category === 'basic_submission' &&
                      s.challenge_id === activeChallengeId &&
                      s.member_id !== currentUser?.id
                    ).sort((a, b) => new Date(b.submission_time) - new Date(a.submission_time));
                    
                    // Find which specific videos the current user already gave feedback on (tracked via first_submission_id)
                    const myFeedbackGiven = submissions.filter(s =>
                      s.submission_category === 'feedback_given' &&
                      s.challenge_id === activeChallengeId &&
                      s.member_id === currentUser?.id
                    );
                    const feedbackGivenToVideoIds = new Set(myFeedbackGiven.map(s => s.first_submission_id).filter(Boolean));
                    
                    if (challengeVideos.length === 0) {
                      return (
                        <div className="text-center py-4 bg-slate-900/30 rounded-lg border border-slate-700/50">
                          <Video className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                          <p className="text-slate-500 text-sm">No submissions in this challenge yet</p>
                        </div>
                      );
                    }
                    
                    // Show ALL individual videos (one person can have multiple videos)
                    return challengeVideos.map((video) => {
                      const member = members.find(m => m.id === video.member_id);
                      const isSelected = newSubmission.first_submission_id === video.id;
                      const isDone = feedbackGivenToVideoIds.has(video.id);
                      const videoId = video.content_url?.includes('youtube.com/watch?v=') 
                        ? video.content_url.split('v=')[1]?.split('&')[0]
                        : video.content_url?.includes('youtu.be/')
                        ? video.content_url.split('youtu.be/')[1]?.split('?')[0]
                        : null;
                      const isUploadedVideo = video.content_url?.startsWith('/api/uploads/');
                      const taskName = challenges.find(c => c.id === video.challenge_id)?.task_details?.find(t => t.task_number === video.task_number)?.title || `Task ${video.task_number}`;
                      
                      return (
                        <div key={video.id} className="space-y-2">
                          <button
                            type="button"
                            disabled={isDone}
                            onClick={() => {
                              if (!isDone) {
                                setNewSubmission({
                                  ...newSubmission, 
                                  feedback_to_member_id: video.member_id,
                                  challenge_id: video.challenge_id,
                                  first_submission_id: video.id
                                });
                              }
                            }}
                            className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                              isDone
                                ? 'bg-green-500/10 border-green-500/30 opacity-60 cursor-not-allowed'
                                : isSelected 
                                ? 'bg-purple-500/20 border-purple-500/50 ring-2 ring-purple-500/30' 
                                : 'bg-slate-900/30 border-slate-700/50 hover:border-purple-500/30'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-20 h-14 rounded bg-slate-800 flex-shrink-0 overflow-hidden">
                                {videoId ? (
                                  <img 
                                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                                    alt="Video thumbnail"
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Video className="w-6 h-6 text-slate-600" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium text-sm truncate ${isDone ? 'text-slate-400 line-through' : 'text-white'}`}>{member?.name || 'Unknown'}</span>
                                  {isDone && <Badge className="bg-green-500/20 text-green-300 text-xs px-1 py-0">Done</Badge>}
                                  {isSelected && !isDone && <CheckCircle2 className="w-4 h-4 text-purple-400 flex-shrink-0" />}
                                </div>
                                <p className="text-slate-400 text-xs truncate">{taskName}</p>
                                {video.content_url && video.content_url !== 'None' && !videoId && !isUploadedVideo && (
                                  <a href={video.content_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:text-blue-300 flex items-center gap-1 mt-0.5" onClick={(e) => e.stopPropagation()}>
                                    <ExternalLink className="w-3 h-3" /> View Link
                                  </a>
                                )}
                              </div>
                            </div>
                          </button>
                          {/* Embedded video player when selected */}
                          {isSelected && !isDone && (
                            <div className="rounded-lg overflow-hidden border border-purple-500/30 bg-slate-900">
                              {videoId ? (
                                <div className="aspect-video">
                                  <iframe
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title={`${member?.name || 'Video'} - ${taskName}`}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  />
                                </div>
                              ) : isUploadedVideo ? (
                                <div className="aspect-video">
                                  <video
                                    src={`${BACKEND_URL}${video.content_url}`}
                                    controls
                                    className="w-full h-full"
                                  >
                                    Your browser does not support video playback.
                                  </video>
                                </div>
                              ) : video.content_url && video.content_url !== 'None' && video.content_url.trim() !== '' ? (
                                <div className="p-4 text-center">
                                  <a href={video.content_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors">
                                    <ExternalLink className="w-4 h-4" />
                                    Open Video in New Tab
                                  </a>
                                  <p className="text-slate-500 text-xs mt-2">Video link: {video.content_url}</p>
                                </div>
                              ) : (
                                <div className="p-4 text-center text-slate-500 text-sm">
                                  No video link available for this submission
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
                
                {/* SPARK Feedback Framework */}
                {newSubmission.feedback_to_member_id && (
                  <div className="space-y-4 pt-3 border-t border-purple-500/20">
                    {/* SPARK Header */}
                    <div className="bg-gradient-to-r from-purple-500/10 to-amber-500/10 rounded-lg p-3 border border-purple-500/20">
                      <p className="text-sm text-purple-200 italic font-medium">
                        "Don't just say what was good... Tell them what worked, what can improve, and how to improve it."
                      </p>
                    </div>

                    {/* Feedback Mode Toggle */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setNewSubmission({...newSubmission, feedback_mode: 'text'})}
                        className={`flex-1 p-2 rounded-lg border text-sm font-medium transition-all ${
                          newSubmission.feedback_mode === 'text'
                            ? 'bg-purple-500/20 border-purple-500/50 text-purple-200'
                            : 'bg-slate-800/30 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        Text Feedback
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewSubmission({...newSubmission, feedback_mode: 'video'})}
                        className={`flex-1 p-2 rounded-lg border text-sm font-medium transition-all ${
                          newSubmission.feedback_mode === 'video'
                            ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-200'
                            : 'bg-slate-800/30 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        Video Feedback
                      </button>
                    </div>

                    {/* Video Link (if video mode) */}
                    {newSubmission.feedback_mode === 'video' && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-cyan-300">Feedback Video Link (30-60 sec)</Label>
                        <Input
                          value={newSubmission.content_url || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, content_url: e.target.value})}
                          placeholder="https://youtube.com/watch?v=... or https://drive.google.com/..."
                          className="bg-slate-900/50 border-cyan-500/20 text-white placeholder:text-slate-500"
                          data-testid="feedback-video-url-input"
                        />
                        <p className="text-xs text-cyan-400/60">Upload to YouTube (unlisted) or Google Drive and paste the link here</p>
                      </div>
                    )}

                    {/* SPARK Fields */}
                    <div className="space-y-3">
                      {/* S - Specific Appreciation */}
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold text-green-300 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center text-green-400 text-xs font-bold">S</span>
                          Specific Appreciation
                          <span className="text-slate-500 font-normal">(Optional)</span>
                        </Label>
                        <textarea
                          value={newSubmission.spark_specific || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, spark_specific: e.target.value})}
                          placeholder="What specifically worked well in this video?"
                          className="w-full h-16 bg-slate-900/50 border border-green-500/20 text-white placeholder:text-slate-500 rounded-lg p-2.5 text-sm focus:border-green-500/50 focus:ring-green-500/20 focus:outline-none resize-none"
                        />
                      </div>

                      {/* P - Point of Improvement */}
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold text-amber-300 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">P</span>
                          Point of Improvement
                          <span className="text-red-400 font-normal">*</span>
                        </Label>
                        <textarea
                          value={newSubmission.spark_improvement || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, spark_improvement: e.target.value})}
                          placeholder="What can be improved?"
                          className="w-full h-16 bg-slate-900/50 border border-amber-500/20 text-white placeholder:text-slate-500 rounded-lg p-2.5 text-sm focus:border-amber-500/50 focus:ring-amber-500/20 focus:outline-none resize-none"
                          required
                        />
                      </div>

                      {/* A - Actionable Suggestion */}
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold text-blue-300 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-xs font-bold">A</span>
                          Actionable Suggestion
                          <span className="text-red-400 font-normal">*</span>
                        </Label>
                        <textarea
                          value={newSubmission.spark_actionable || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, spark_actionable: e.target.value})}
                          placeholder="What exactly can they do to improve?"
                          className="w-full h-16 bg-slate-900/50 border border-blue-500/20 text-white placeholder:text-slate-500 rounded-lg p-2.5 text-sm focus:border-blue-500/50 focus:ring-blue-500/20 focus:outline-none resize-none"
                          required
                        />
                      </div>

                      {/* R - Reflection / Insight */}
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold text-pink-300 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-pink-500/20 flex items-center justify-center text-pink-400 text-xs font-bold">R</span>
                          Reflection / Insight
                          <span className="text-slate-500 font-normal">(Optional)</span>
                        </Label>
                        <textarea
                          value={newSubmission.spark_reflection || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, spark_reflection: e.target.value})}
                          placeholder="What did you observe or feel?"
                          className="w-full h-16 bg-slate-900/50 border border-pink-500/20 text-white placeholder:text-slate-500 rounded-lg p-2.5 text-sm focus:border-pink-500/50 focus:ring-pink-500/20 focus:outline-none resize-none"
                        />
                      </div>

                      {/* K - Keep / Continue */}
                      <div className="space-y-1">
                        <Label className="text-xs font-semibold text-emerald-300 flex items-center gap-1.5">
                          <span className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">K</span>
                          Keep / Continue
                          <span className="text-slate-500 font-normal">(Optional)</span>
                        </Label>
                        <textarea
                          value={newSubmission.spark_continue || ''}
                          onChange={(e) => setNewSubmission({...newSubmission, spark_continue: e.target.value})}
                          placeholder="What should they continue doing?"
                          className="w-full h-16 bg-slate-900/50 border border-emerald-500/20 text-white placeholder:text-slate-500 rounded-lg p-2.5 text-sm focus:border-emerald-500/50 focus:ring-emerald-500/20 focus:outline-none resize-none"
                        />
                      </div>
                    </div>

                    {/* Fields filled indicator */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">
                        {[newSubmission.spark_specific, newSubmission.spark_improvement, newSubmission.spark_actionable, newSubmission.spark_reflection, newSubmission.spark_continue].filter(Boolean).length}/5 SPARK fields filled
                      </span>
                      <span className="text-slate-500">Min 3 required (P + A mandatory)</span>
                    </div>

                    <p className="text-xs text-slate-500">Your feedback will be reviewed by an admin before 200 SP is awarded.</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Submission Method - Redesigned */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-emerald-400" />
                How did you share your content?
              </Label>
              
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'social_media', icon: '📱', label: 'Social Media', desc: 'Instagram, LinkedIn, etc.', color: 'from-pink-500/20 to-rose-500/20', border: 'border-pink-500/30', active: 'ring-pink-500/50' },
                  { value: 'whatsapp', icon: '💬', label: 'WhatsApp', desc: 'Community Group', color: 'from-emerald-500/20 to-green-500/20', border: 'border-emerald-500/30', active: 'ring-emerald-500/50' },
                  { value: 'live_huddle', icon: '🎥', label: 'Live Huddle', desc: 'Live Session', color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30', active: 'ring-blue-500/50' }
                ].map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => setNewSubmission({...newSubmission, submission_method: method.value, content_url: method.value !== 'social_media' ? '' : newSubmission.content_url})}
                    className={`relative p-4 rounded-xl border transition-all duration-200 text-left
                      ${newSubmission.submission_method === method.value 
                        ? `bg-gradient-to-br ${method.color} ${method.border} ring-2 ${method.active} scale-[1.02]` 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/50'
                      }`}
                  >
                    <div className="text-2xl mb-2">{method.icon}</div>
                    <div className="font-medium text-white text-sm">{method.label}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{method.desc}</div>
                    {newSubmission.submission_method === method.value && (
                      <div className="absolute top-2 right-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Social Media URL - External Links Only */}
              {newSubmission.submission_method === 'social_media' && (
                <div className="mt-4 p-4 bg-gradient-to-r from-pink-500/5 to-rose-500/5 border border-pink-500/20 rounded-xl space-y-3">
                  <Label className="text-sm font-medium text-pink-300 block">
                    Share your content link
                  </Label>
                  <Input
                    type="url"
                    value={newSubmission.content_url}
                    onChange={(e) => setNewSubmission({...newSubmission, content_url: e.target.value})}
                    placeholder="https://youtube.com/... or https://drive.google.com/..."
                    className="bg-slate-900/50 border-pink-500/20 text-white placeholder:text-slate-500 h-11 rounded-lg focus:border-pink-500/50 focus:ring-pink-500/20"
                    data-testid="content-url-input"
                  />
                  <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-blue-300 font-medium mb-1">How to share your video:</p>
                    <ul className="text-xs text-blue-200/80 space-y-1 list-disc list-inside">
                      <li>Upload to <strong>YouTube as Unlisted</strong> and paste the link</li>
                      <li>Or upload to <strong>Google Drive</strong>, set sharing to "Anyone with the link", and paste the link</li>
                      <li>Instagram / other social media post links also work</li>
                    </ul>
                  </div>
                </div>
              )}
              
              {newSubmission.submission_method === 'whatsapp' && (
                <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <p className="text-sm text-emerald-300">Make sure you have uploaded your content to the community WhatsApp group</p>
                </div>
              )}
              
              {newSubmission.submission_method === 'live_huddle' && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  <p className="text-sm text-blue-300">This submission was delivered during a live huddle session</p>
                </div>
              )}
            </div>

            {/* Submission Date & Time */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                Submission Date & Time
              </Label>
              <Input
                type="datetime-local"
                value={newSubmission.submission_time}
                onChange={(e) => setNewSubmission({...newSubmission, submission_time: e.target.value})}
                className="bg-slate-800/50 border-slate-600/50 text-white h-12 rounded-xl hover:border-slate-500 transition-colors [color-scheme:dark]"
              />
            </div>

            {/* Story Points Preview - Dynamic based on category */}
            <div className={`p-4 border rounded-xl ${
              newSubmission.submission_category === 'improvement_evidence' 
                ? 'bg-gradient-to-r from-purple-500/10 via-fuchsia-500/10 to-purple-500/10 border-purple-500/20'
                : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                ? 'bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-blue-500/10 border-blue-500/20'
                : 'bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-amber-500/20'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    newSubmission.submission_category === 'improvement_evidence' 
                      ? 'bg-purple-500/20'
                      : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                      ? 'bg-blue-500/20'
                      : 'bg-amber-500/20'
                  }`}>
                    <Award className={`w-5 h-5 ${
                      newSubmission.submission_category === 'improvement_evidence' 
                        ? 'text-purple-400'
                        : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                        ? 'text-blue-400'
                        : 'text-amber-400'
                    }`} />
                  </div>
                  <div>
                    <p className={`font-medium ${
                      newSubmission.submission_category === 'improvement_evidence' 
                        ? 'text-purple-200'
                        : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                        ? 'text-blue-200'
                        : 'text-amber-200'
                    }`}>Story Points Reward</p>
                    <p className={`text-xs ${
                      newSubmission.submission_category === 'improvement_evidence' 
                        ? 'text-purple-300/70'
                        : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                        ? 'text-blue-300/70'
                        : 'text-amber-300/70'
                    }`}>
                      {newSubmission.submission_category === 'improvement_evidence' && 'Show your growth for maximum points!'}
                      {newSubmission.submission_category === 'feedback_request' && 'Earn bonus points for seeking feedback!'}
                      {newSubmission.submission_category === 'live_session' && 'Live participation bonus points!'}
                      {newSubmission.submission_category === 'basic_submission' && 'You will earn points for this submission'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${
                    newSubmission.submission_category === 'improvement_evidence' 
                      ? 'text-purple-400'
                      : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                      ? 'text-blue-400'
                      : 'text-amber-400'
                  }`}>
                    +{newSubmission.submission_category === 'improvement_evidence' ? '500' : 
                      newSubmission.submission_category === 'feedback_given' ? '200' :
                      newSubmission.submission_category === 'feedback_request' ? '300' : 
                      newSubmission.submission_category === 'live_session' ? '300' : '100'}
                  </p>
                  <p className={`text-xs ${
                    newSubmission.submission_category === 'improvement_evidence' 
                      ? 'text-purple-300/70'
                      : newSubmission.submission_category === 'feedback_request' || newSubmission.submission_category === 'live_session'
                      ? 'text-blue-300/70'
                      : 'text-amber-300/70'
                  }`}>Story Points</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowSubmissionDialog(false)} 
                className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white px-6 rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white px-8 rounded-xl shadow-lg shadow-violet-500/25 transition-all hover:shadow-violet-500/40"
              >
                <Rocket className="w-4 h-4 mr-2" />
                Submit Content
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Export Dashboard</DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose your preferred export format
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3">
              <Button
                onClick={handleExportJSON}
                variant="outline"
                className="justify-start p-4 h-auto border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                <FileDown className="w-8 h-8 text-blue-400" />
                <div className="text-center">
                  <div className="font-semibold">Export as JSON</div>
                  <div className="text-xs text-slate-400">Raw data export</div>
                </div>
              </Button>
              
              <Button
                onClick={handleExportImage}
                variant="outline"
                className="justify-start p-4 h-auto border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                <FileDown className="w-8 h-8 text-green-400" />
                <div className="text-center">
                  <div className="font-semibold">Export as Image</div>
                  <div className="text-xs text-slate-400">Screenshot of dashboard</div>
                </div>
              </Button>
              
              <Button
                onClick={handleExportPDF}
                variant="outline"
                className="justify-start p-4 h-auto border-slate-600 text-slate-200 hover:bg-slate-700"
              >
                <FileDown className="w-8 h-8 text-red-400" />
                <div className="text-center">
                  <div className="font-semibold">Export as PDF</div>
                  <div className="text-xs text-slate-400">Formatted report</div>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task-by-Task Progress Dialog */}
      <Dialog open={!!viewingTaskDetails} onOpenChange={() => {
        setViewingTaskDetails(null);
        setChallengeTaskProgress(null);
      }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center space-x-2">
              <List className="w-6 h-6 text-blue-400" />
              <span>{viewingTaskDetails?.title} - Task Progress</span>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Your task-by-task progress and submission history
            </DialogDescription>
          </DialogHeader>
          
          {challengeTaskProgress ? (
            <div className="space-y-4">
              {/* Progress Summary */}
              <Card className="bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-blue-500/30">
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-300">{challengeTaskProgress.completed_tasks}</p>
                      <p className="text-xs text-slate-400">Tasks Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-white">{challengeTaskProgress.total_tasks}</p>
                      <p className="text-xs text-slate-400">Total Tasks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-400">{challengeTaskProgress.total_story_points}</p>
                      <p className="text-xs text-slate-400">Story Points</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">{challengeTaskProgress.progress_percentage}%</p>
                      <p className="text-xs text-slate-400">Progress</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Task List */}
              <div className="space-y-3">
                {challengeTaskProgress.task_progress?.map((task) => (
                  <Card 
                    key={task.task_number} 
                    className={`bg-slate-700/50 border-slate-600 ${task.is_completed ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-slate-500'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${task.is_completed ? 'bg-green-500/30 text-green-400' : 'bg-slate-600 text-slate-400'}`}>
                              {task.is_completed ? <CheckCircle2 className="w-5 h-5" /> : <span className="text-sm font-medium">{task.task_number}</span>}
                            </div>
                            <div>
                              <h4 className="font-semibold text-white">{task.title}</h4>
                              {task.description && (
                                <p className="text-sm text-slate-400 mt-1">{task.description}</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Video Preview if available */}
                          {task.video_url && (
                            <div className="mt-3 ml-11">
                              <a 
                                href={task.video_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 text-sm"
                              >
                                <Play className="w-4 h-4" />
                                <span>Watch Reference Video</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                          
                          {/* Tips if available */}
                          {task.tips && (
                            <div className="mt-2 ml-11 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                              <p className="text-xs text-amber-300">💡 {task.tips}</p>
                            </div>
                          )}
                          
                          {/* Submissions for this task */}
                          {task.submissions && task.submissions.length > 0 && (
                            <div className="mt-3 ml-11 space-y-2">
                              <p className="text-xs text-slate-400 font-medium">Your Submissions:</p>
                              {task.submissions.map((sub, idx) => (
                                <div key={idx} className="flex items-center space-x-3 text-sm bg-slate-600/50 p-2 rounded-lg">
                                  <Badge variant="secondary" className="bg-green-500/30 text-green-300 text-xs">
                                    +{sub.story_points_awarded} SP
                                  </Badge>
                                  <span className="text-slate-300 capitalize">
                                    {sub.submission_method === 'social_media' ? '📱 Social Media' : 
                                     sub.submission_method === 'whatsapp' ? '💬 WhatsApp' : 
                                     '🎥 Live Huddle'}
                                  </span>
                                  {sub.content_url && (
                                    <a href={sub.content_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                  <span className="text-slate-500 text-xs">
                                    {new Date(sub.submission_time).toLocaleDateString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Submit Button for incomplete tasks */}
                        {!task.is_completed && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setViewingTaskDetails(null);
                              setChallengeTaskProgress(null);
                              setNewSubmission({
                                ...newSubmission,
                                challenge_id: viewingTaskDetails.id,
                                task_number: task.task_number,
                                member_id: currentUser?.id || ""
                              });
                              setShowSubmissionDialog(true);
                            }}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Submit
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-4"></div>
              <p className="text-slate-400">Loading task progress...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <AdminLoginDialog
        isOpen={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onLogin={setIsAuthenticated}
        actionName={loginAction}
      />
      
      <MemberLoginDialog
        isOpen={showMemberLoginDialog}
        onClose={() => setShowMemberLoginDialog(false)}
        onLogin={handleMemberLogin}
        onForgotPassword={() => setShowPasswordResetRequestDialog(true)}
      />
      
      {/* Password Reset Request Dialog */}
      <PasswordResetRequestDialog
        isOpen={showPasswordResetRequestDialog}
        onClose={() => setShowPasswordResetRequestDialog(false)}
        API={API}
      />
      
      {/* Password Reset Confirm Dialog */}
      <PasswordResetConfirmDialog
        isOpen={showPasswordResetConfirmDialog}
        onClose={() => {
          setShowPasswordResetConfirmDialog(false);
          setPasswordResetToken(null);
        }}
        token={passwordResetToken}
        API={API}
        onSuccess={() => setShowMemberLoginDialog(true)}
      />

      {/* Welcome Popup Dialog */}
      <Dialog open={showWelcomePopup} onOpenChange={setShowWelcomePopup}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold text-white flex items-center gap-3">
              <span className="text-2xl sm:text-3xl">👋</span>
              Welcome to CSH Story Utsav!
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Your skill-based monthly practice and growth platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Welcome Video - Smaller on mobile */}
            {(() => {
              const activeChallenge = challenges.find(c => c.status === 'active');
              const videoUrl = activeChallenge?.welcome_video_url;
              
              return videoUrl ? (
                <div className="aspect-video rounded-xl overflow-hidden bg-slate-800 border border-slate-700 max-h-[200px] sm:max-h-[280px]">
                  <iframe
                    src={videoUrl.includes('youtube.com') 
                      ? videoUrl.replace('watch?v=', 'embed/').split('&')[0]
                      : videoUrl.includes('vimeo.com')
                      ? videoUrl.replace('vimeo.com/', 'player.vimeo.com/video/')
                      : videoUrl}
                    title="Welcome Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="aspect-video rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30 flex items-center justify-center max-h-[150px] sm:max-h-[200px]">
                  <div className="text-center">
                    <Play className="w-16 h-16 text-violet-400 mx-auto mb-3" />
                    <p className="text-slate-400">Welcome video coming soon</p>
                  </div>
                </div>
              );
            })()}
            
            {/* Portal Overview */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-400" />
                How It Works
              </h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  Complete daily prompts and earn Story Points
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  Reach 1500 SP to become a Monthly Finisher ⭐
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  Reach 2100 SP to become an Elite Finisher 🏆
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-400">✓</span>
                  Request feedback (+300 SP) and show improvement (+500 SP)
                </li>
              </ul>
            </div>
            
            {/* Track Selection */}
            <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl">
              <h3 className="font-semibold text-amber-200 mb-3 flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                Choose Your Track for This Month
              </h3>
              <p className="text-sm text-amber-300/70 mb-4">
                Your track determines the prompts you&apos;ll see. Once you make a submission, your track is locked for the month.
              </p>
              
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'career', label: 'Career', desc: 'Career Confidence', icon: '💼', color: 'from-blue-500/20 to-cyan-500/20', border: 'border-blue-500/30' },
                  { value: 'business', label: 'Business', desc: 'Business Influence', icon: '📈', color: 'from-green-500/20 to-emerald-500/20', border: 'border-green-500/30' },
                  { value: 'coach', label: 'Coach', desc: 'Authority & Impact', icon: '🎯', color: 'from-purple-500/20 to-fuchsia-500/20', border: 'border-purple-500/30' }
                ].map((track) => (
                  <button
                    key={track.value}
                    onClick={() => handleTrackSelection(track.value)}
                    disabled={currentUser?.track_locked_month === new Date().toISOString().slice(0, 7)}
                    className={`p-4 rounded-xl border transition-all text-center ${
                      selectedTrackForMonth === track.value
                        ? `bg-gradient-to-br ${track.color} ${track.border} ring-2 ring-white/20`
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    } ${currentUser?.track_locked_month === new Date().toISOString().slice(0, 7) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className="text-2xl">{track.icon}</span>
                    <p className="font-medium text-white mt-2">{track.label}</p>
                    <p className="text-xs text-slate-400">{track.desc}</p>
                  </button>
                ))}
              </div>
              
              {currentUser?.track_locked_month === new Date().toISOString().slice(0, 7) && (
                <p className="text-xs text-amber-400 mt-3 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Track locked - you&apos;ve made a submission this month
                </p>
              )}
            </div>
          </div>
          
          <div className="flex justify-between items-center pt-4 border-t border-slate-700">
            <Button
              variant="ghost"
              onClick={handleHideWelcomePopup}
              className="text-slate-400 hover:text-white"
            >
              Don&apos;t show again
            </Button>
            <Button
              onClick={() => setShowWelcomePopup(false)}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500"
            >
              Get Started
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Mass Import Dialog */}
      <Dialog open={showMassImportDialog} onOpenChange={setShowMassImportDialog}>
        <DialogContent className="max-w-2xl bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" />
              Mass Import Users
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Import multiple users from CSV data. Users will need to set their password on first login.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300 mb-2 block">CSV Format</Label>
              <p className="text-xs text-slate-500 mb-2">name, email, track (optional: career/business/coach)</p>
              <Textarea
                value={importCsvText}
                onChange={(e) => setImportCsvText(e.target.value)}
                placeholder={`John Doe, john@example.com, career
Jane Smith, jane@example.com, business
Bob Wilson, bob@example.com, coach`}
                className="h-48 bg-slate-900 border-slate-600 text-white font-mono text-sm"
              />
            </div>
            
            {importResults && (
              <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-white mb-2">Import Results</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-green-400">{importResults.created_count}</p>
                    <p className="text-xs text-slate-400">Created</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-400">{importResults.skipped_count}</p>
                    <p className="text-xs text-slate-400">Skipped</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{importResults.error_count}</p>
                    <p className="text-xs text-slate-400">Errors</p>
                  </div>
                </div>
                
                {importResults.skipped?.length > 0 && (
                  <div className="mt-3 text-xs">
                    <p className="text-yellow-400 font-medium">Skipped:</p>
                    {importResults.skipped.slice(0, 5).map((s, i) => (
                      <p key={i} className="text-slate-400">{s.email}: {s.reason}</p>
                    ))}
                  </div>
                )}
                
                {importResults.errors?.length > 0 && (
                  <div className="mt-3 text-xs">
                    <p className="text-red-400 font-medium">Errors:</p>
                    {importResults.errors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-slate-400">{e.email}: {e.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowMassImportDialog(false);
                setImportCsvText("");
                setImportResults(null);
              }}
              className="border-slate-600 text-slate-300"
            >
              Close
            </Button>
            <Button
              onClick={handleMassImport}
              disabled={isImporting || !importCsvText.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isImporting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Users
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Team Dialog */}
      <Dialog open={showTeamDialog} onOpenChange={setShowTeamDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2">
              🏆 Create New Team
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new team for the CSH Story Premier League
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTeam} className="space-y-4">
            <div>
              <Label className="text-slate-300">Team Name</Label>
              <Input
                value={newTeam.name}
                onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                placeholder="Enter team name"
                className="bg-slate-700 border-slate-600 text-white"
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowTeamDialog(false)}
                className="border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Team
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={!!editingTeam} onOpenChange={() => setEditingTeam(null)}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-blue-400 flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Team
            </DialogTitle>
          </DialogHeader>
          {editingTeam && (
            <form onSubmit={handleUpdateTeam} className="space-y-4">
              <div>
                <Label className="text-slate-300">Team Name</Label>
                <Input
                  value={editingTeam.name}
                  onChange={(e) => setEditingTeam({ ...editingTeam, name: e.target.value })}
                  className="bg-slate-700 border-slate-600 text-white"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingTeam(null)}
                  className="border-slate-600 text-slate-300"
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Team Detail Dialog */}
      <Dialog open={showTeamDetailDialog} onOpenChange={(open) => {
        setShowTeamDetailDialog(open);
        if (!open) setSelectedTeam(null);
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-400 flex items-center gap-2 text-xl">
              🏆 {selectedTeam?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Team details and member contributions
              {selectedTeam?.challenge_id && (
                <span className="block mt-1 text-blue-400">
                  For: {challenges.find(c => c.id === selectedTeam.challenge_id)?.title || "Selected Challenge"}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedTeam && (
            <div className="space-y-4">
              {/* Team Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-amber-400">{selectedTeam.total_team_points?.toLocaleString() || 0}</p>
                  <p className="text-slate-400 text-sm">Challenge Points</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-400">{selectedTeam.member_count || 0}</p>
                  <p className="text-slate-400 text-sm">Members</p>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                  <p className="text-lg font-bold text-green-400">{selectedTeam.captain_name || "None"}</p>
                  <p className="text-slate-400 text-sm">Captain</p>
                </div>
              </div>

              {/* Team Size Info */}
              <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600">
                <p className="text-slate-300 text-sm">
                  Team Size: {selectedTeam.member_count || 0} / 8 players 
                  {(selectedTeam.member_count || 0) < 3 && (
                    <span className="text-amber-400 ml-2">(Minimum 3 required)</span>
                  )}
                </p>
              </div>

              {/* Members List */}
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Team Players - Contributions This Challenge
                </h4>
                <div className="space-y-2">
                  {selectedTeam.members?.length > 0 ? (
                    selectedTeam.members.map((member, index) => (
                      <div
                        key={member.id}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          member.is_captain
                            ? "bg-amber-900/30 border-amber-600/50"
                            : "bg-slate-700/30 border-slate-600/50"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            member.is_captain ? "bg-amber-500 text-amber-900" : "bg-slate-600 text-slate-300"
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <span className="text-white font-medium flex items-center gap-2">
                              {member.name}
                              {member.is_captain && (
                                <Badge className="bg-amber-600 text-amber-100 text-xs">Captain</Badge>
                              )}
                            </span>
                            <p className="text-slate-400 text-sm">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-amber-400 font-bold">{(member.challenge_points ?? member.total_story_points)?.toLocaleString() || 0}</p>
                            <p className="text-slate-500 text-xs">Challenge Points</p>
                          </div>
                          {(isAuthenticated || currentUser?.is_admin) && (
                            <div className="flex gap-1">
                              {!member.is_captain && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleSetTeamCaptain(selectedTeam.id, member.id)}
                                  className="text-amber-400 hover:bg-amber-900/30 h-8 px-2"
                                  title="Make Captain"
                                >
                                  👑
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveMemberFromTeam(selectedTeam.id, member.id, member.name)}
                                className="text-red-400 hover:bg-red-900/30 h-8 px-2"
                                title="Remove from Team"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-slate-400 text-center py-4">No players in this team yet</p>
                  )}
                </div>
              </div>

              {/* Add Member Button */}
              {(isAuthenticated || currentUser?.is_admin) && selectedTeam.member_count < 8 && (
                <Button
                  onClick={() => {
                    setTeamToAddMember(selectedTeam);
                    setShowAddMemberToTeamDialog(true);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Player to Team
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member to Team Dialog */}
      <Dialog open={showAddMemberToTeamDialog} onOpenChange={(open) => {
        setShowAddMemberToTeamDialog(open);
        if (!open) setTeamToAddMember(null);
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-green-400 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Add Player to {teamToAddMember?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a member to add to this team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {getMembersWithoutTeam().length === 0 ? (
              <p className="text-slate-400 text-center py-4">All members are already assigned to teams</p>
            ) : (
              getMembersWithoutTeam().map((member) => (
                <div
                  key={member.id}
                  className="p-3 rounded-lg border border-slate-600 bg-slate-700/30 flex items-center justify-between hover:bg-slate-700/50 cursor-pointer"
                  onClick={() => teamToAddMember && handleAddMemberToTeam(teamToAddMember.id, member.id)}
                >
                  <div>
                    <p className="text-white font-medium">{member.name}</p>
                    <p className="text-slate-400 text-sm">{member.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-amber-400 font-bold">{member.total_story_points?.toLocaleString() || 0} SP</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;